// Feature pages: single source of truth for /features/:slug, the hub, sitemap,
// prerender and JSON-LD. Pure data (no Vue/DOM) - imported by vite.config too.
// Copy rules: truthful (feature exists in this codebase), no em dashes.
// EN + PL inline; de/es/uk come from i18n/<lang>.ts packs (same structure).
import { PACKS } from './i18n'

/** Marketing locales: EN at the root, the rest under /<code>. */
export const MK_LOCALES = ['en', 'pl', 'de', 'es', 'uk'] as const
export type MkLocale = (typeof MK_LOCALES)[number]
/** Locales whose copy lives in i18n packs. */
export type PackLocale = Exclude<MkLocale, 'en' | 'pl'>

export interface FeatureCopy {
  /** Short label for nav, cards, breadcrumbs. */
  name: string
  /** SEO <title> without the " | sign.yt" suffix; full title <= 60 chars. */
  title: string
  /** Meta description, 140-160 chars. */
  desc: string
  h1: string
  lead: string
  /** body is plain text; blank line = new paragraph. */
  sections: { h2: string; body: string; requires?: Capability }[]
  faq: { q: string; a: string }[]
}

/** Platform capability flags from GET /public/capabilities. */
export type Capability =
  | 'pq_seal' | 'aws_kms' | 'bitcoin_anchoring' | 'trusted_timestamps'
  | 'eidas_validation' | 'sms' | 'wezel_podpisu' | 'qes'

/** Hub sections, in render order. */
export const GROUPS = ['trust', 'brand', 'docs', 'dev'] as const
export type FeatureGroup = (typeof GROUPS)[number]
const GROUPS_ENPL: Record<FeatureGroup, Record<'en' | 'pl', string>> = {
  trust: { en: 'Trust & legal', pl: 'Zaufanie i prawo' },
  brand: { en: 'Your brand', pl: 'Twoja marka' },
  docs: { en: 'Working with documents', pl: 'Praca z dokumentami' },
  dev: { en: 'For developers', pl: 'Dla programistów' },
}
export const GROUP_LABELS = Object.fromEntries(GROUPS.map((g) => [g, {
  ...GROUPS_ENPL[g], de: PACKS.de.groups[g], es: PACKS.es.groups[g], uk: PACKS.uk.groups[g],
}])) as Record<FeatureGroup, Record<MkLocale, string>>

export interface Feature {
  /** English slug, shared by every locale (/features/:slug, /pl/features/:slug, ...). */
  slug: string
  /** Key of ICONS in components/admin/iconRegistry.ts (e.g. 'Globe'). */
  icon: string
  group: FeatureGroup
  /** Wide highlighted hub card with a visual (the feature's example). */
  wide?: boolean
  /** Shown only when the platform has this capability; never prerendered. */
  requires?: Capability
  copy: Record<MkLocale, FeatureCopy>
}

/** Items without `requires` are always shown (and prerendered). */
export const visible = (x: { requires?: Capability }, has: (c: Capability) => boolean = () => false) =>
  !x.requires || has(x.requires)

type RawFeature = Omit<Feature, 'copy'> & { copy: Record<'en' | 'pl', FeatureCopy> }

const RAW: RawFeature[] = [
  {
    slug: 'custom-domains',
    icon: 'Globe',
    group: 'brand',
    copy: {
      en: {
        name: 'Custom domains',
        title: 'Custom signing domains for every client',
        desc: 'Run e-signatures on your own domain, like sign.yourcompany.com. Point one CNAME record, we verify DNS and issue the TLS certificate automatically.',
        h1: 'Your domain, your brand, our signing engine',
        lead: 'Recipients open signing links on sign.yourcompany.com, not on ours. Each organization gets its own verified domain with an automatic TLS certificate, on the same shared platform.',
        sections: [
          {
            h2: 'Set up in minutes',
            body: 'Add the domain in your organization settings and create one CNAME record at your DNS provider. Once the record resolves, sign.yt verifies it and issues a Let\'s Encrypt certificate on the first request.\n\nNo separate deployment, no certificate files to upload or renew.',
          },
          {
            h2: 'Every request lands in the right organization',
            body: 'The platform resolves the organization from the Host header of each request. Signing pages, emails and the login screen on your domain show your logo and colours, and data never crosses between tenants.',
          },
          {
            h2: 'Built for operators with many brands',
            body: 'Virtual offices, coworking operators and accounting firms often run several brands. Each organization can use its own domain, branding and email sender, while you manage everything from one account.',
          },
        ],
        faq: [
          {
            q: 'Which plans include a custom domain?',
            a: 'Custom domains are available from the Professional plan upward.',
          },
          {
            q: 'Do I need to manage TLS certificates?',
            a: 'No. Certificates are issued and renewed automatically once your DNS record points to the platform.',
          },
          {
            q: 'Can I keep using the sign.yt address?',
            a: 'Yes. Links on the platform domain keep working; the custom domain is an addition, not a migration.',
          },
          {
            q: 'Do verification links use my domain too?',
            a: 'The QR code and short link on each signed document point to verify.sign.yt by default. Moving them to your own domain is part of the paid Full customization add-on.',
          },
        ],
      },
      pl: {
        name: 'Własne domeny',
        title: 'Własna domena do podpisów dla każdego klienta',
        desc: 'Podpisy elektroniczne pod Twoją domeną, np. sign.twojafirma.pl. Dodajesz rekord CNAME, my weryfikujemy DNS i automatycznie wystawiamy certyfikat TLS.',
        h1: 'Twoja domena, Twoja marka, nasz silnik podpisów',
        lead: 'Odbiorcy otwierają linki do podpisu pod adresem sign.twojafirma.pl, a nie naszym. Każda organizacja ma własną zweryfikowaną domenę z automatycznym certyfikatem TLS, na jednej wspólnej platformie.',
        sections: [
          {
            h2: 'Konfiguracja w kilka minut',
            body: 'Dodajesz domenę w ustawieniach organizacji i tworzysz jeden rekord CNAME u swojego dostawcy DNS. Gdy rekord się rozpropaguje, sign.yt go weryfikuje i przy pierwszym żądaniu wystawia certyfikat Let\'s Encrypt.\n\nBez osobnego wdrożenia i bez ręcznego wgrywania czy odnawiania certyfikatów.',
          },
          {
            h2: 'Każde żądanie trafia do właściwej organizacji',
            body: 'Platforma rozpoznaje organizację po nagłówku Host każdego żądania. Strony podpisu, e-maile i ekran logowania pod Twoją domeną pokazują Twoje logo i kolory, a dane nigdy nie mieszają się między klientami.',
          },
          {
            h2: 'Dla operatorów z wieloma markami',
            body: 'Biura wirtualne, coworkingi i biura rachunkowe często prowadzą kilka marek. Każda organizacja może mieć własną domenę, branding i nadawcę e-maili, a Ty zarządzasz wszystkim z jednego konta.',
          },
        ],
        faq: [
          {
            q: 'W których planach jest własna domena?',
            a: 'Własna domena jest dostępna od planu Professional wzwyż.',
          },
          {
            q: 'Czy muszę zarządzać certyfikatami TLS?',
            a: 'Nie. Certyfikaty są wystawiane i odnawiane automatycznie, gdy rekord DNS wskazuje na platformę.',
          },
          {
            q: 'Czy adres sign.yt nadal działa?',
            a: 'Tak. Linki w domenie platformy działają dalej; własna domena to dodatek, a nie migracja.',
          },
          {
            q: 'Czy linki weryfikacyjne też działają w mojej domenie?',
            a: 'Kod QR i krótki link na każdym podpisanym dokumencie domyślnie prowadzą do verify.sign.yt. Przeniesienie ich do Twojej domeny to część płatnego dodatku Pełna personalizacja.',
          },
        ],
      },
    },
  },
  {
    slug: 'white-label-branding',
    icon: 'Sparkles',
    group: 'brand',
    copy: {
      en: {
        name: 'White-label branding',
        title: 'White-label e-signature branding',
        desc: 'Your logo, colours and one of four page themes on every signing page, verification page and email. Teams can have their own look, or you lock it.',
        h1: 'Signing pages that look like your company',
        lead: 'Recipients see your logo, your colours and your name from the first email to the signed PDF. sign.yt stays in the background.',
        sections: [
          {
            h2: 'Your brand on every touchpoint',
            body: 'Upload a logo, set a brand name and a primary colour, and recipients see them on the signing page and on the public verification page of every signed document. The same settings flow into notification emails, together with your sender name and email footer. The login page also takes a favicon, a secondary colour and a short welcome text.\n\nPick one of four page themes: classic, minimal, dark or bold. Each theme works with your brand colour, adjusted where needed so text keeps readable contrast.\n\nRecipients recognise who is asking them to sign, which builds trust and cuts the number of "is this phishing?" replies your team has to answer.',
          },
          {
            h2: 'Different look for each team',
            body: 'Running several locations or brands inside one organization? Each team can override the logo, colours, theme and sender name. A document sent by a team shows that team\'s look on the signing page, on the verification page and in its emails, so a contract from your Warsaw office looks different from one sent in Kraków.\n\nWhen you need consistency, an organization admin can lock team branding with one switch, and every team falls back to the organization look everywhere.',
          },
          {
            h2: 'No platform badge on paid plans',
            body: 'On the Starter plan the login and organization pages show a small "Powered by sign.yt" note. From the Professional plan upward that note disappears, so the experience is fully yours. Combine it with a custom domain and recipients sign on your address, while sign.yt stays in the background.\n\nEvery signed document carries a QR code and a short link such as verify.sign.yt/k3Fq9ZxT2mPa7LwB to its public verification page. With the paid Full customization add-on, these links use your own domain as well.\n\nBranding is resolved in one place, from team to organization to platform defaults, so emails, signing pages and verification pages stay consistent without anyone copying settings by hand.',
          },
        ],
        faq: [
          {
            q: 'What can I customise?',
            a: 'Logo, brand name, primary colour, page theme, email sender name and email footer. The login page also takes a favicon, a secondary colour and a welcome text.',
          },
          {
            q: 'Which page themes can I choose?',
            a: 'Four: classic, minimal, dark and bold. The theme applies to the signing page and the public verification page.',
          },
          {
            q: 'Can each team have its own branding?',
            a: 'Yes. A team can override the logo, colours, theme and sender name. Its documents show that look on the signing page, the verification page and in emails, unless an admin locks team branding for the whole organization.',
          },
          {
            q: 'Does branding apply to emails as well?',
            a: 'Yes. Notification emails use the organization branding, or the team override when there is one, including the sender name and footer.',
          },
          {
            q: 'Where does "Powered by sign.yt" appear?',
            a: 'Only on the Starter plan, on the login and organization pages. Professional and higher plans remove it.',
          },
          {
            q: 'Where do verification links point?',
            a: 'By default to verify.sign.yt, a short address that opens the document\'s verification page in your branding. Verification links on your own domain are part of the paid Full customization add-on.',
          },
        ],
      },
      pl: {
        name: 'Własna marka i branding',
        title: 'Podpis elektroniczny z Twoim logo i kolorami',
        desc: 'Twoje logo, kolory i jeden z czterech motywów na stronie podpisu, stronie weryfikacji i w e-mailach. Zespoły mogą mieć własny wygląd albo go blokujesz.',
        h1: 'Strony do podpisu, które wyglądają jak Twoja firma',
        lead: 'Odbiorca widzi Twoje logo, Twoje kolory i Twoją nazwę od pierwszego e-maila aż po podpisany PDF. sign.yt pozostaje w tle.',
        sections: [
          {
            h2: 'Twoja marka w każdym punkcie styku',
            body: 'Wgrywasz logo, ustawiasz nazwę marki i kolor główny, a odbiorca widzi je na stronie podpisu i na publicznej stronie weryfikacji każdego podpisanego dokumentu. Te same ustawienia trafiają do powiadomień e-mail, razem z nazwą nadawcy i stopką wiadomości. Strona logowania przyjmuje też favicon, kolor dodatkowy i krótki tekst powitalny.\n\nWybierasz jeden z czterech motywów strony: klasyczny, minimalny, ciemny albo wyrazisty. Każdy działa z Twoim kolorem marki, w razie potrzeby skorygowanym tak, żeby tekst pozostał czytelny.\n\nOdbiorca od razu wie, kto prosi go o podpis. To buduje zaufanie i ogranicza pytania w stylu "czy to nie phishing?", na które musi odpowiadać Twój zespół.',
          },
          {
            h2: 'Inny wygląd dla każdego zespołu',
            body: 'Masz kilka lokalizacji albo kilka marek w jednej organizacji? Każdy zespół może nadpisać logo, kolory, motyw i nazwę nadawcy. Dokument wysłany przez zespół ma jego wygląd na stronie podpisu, na stronie weryfikacji i w e-mailach, więc umowa z biura w Warszawie wygląda inaczej niż z biura w Krakowie.\n\nGdy zależy Ci na spójności, administrator organizacji jednym przełącznikiem blokuje branding zespołów i wszystkie wszędzie korzystają z wyglądu organizacji.\n\nTo dobre rozwiązanie dla sieci franczyzowych i biur z wieloma lokalizacjami.',
          },
          {
            h2: 'Bez znaczka platformy w płatnych planach',
            body: 'W planie Starter strona logowania i strony organizacji pokazują dyskretną informację "Powered by sign.yt". Od planu Professional wzwyż znika ona całkowicie. W połączeniu z własną domeną odbiorcy podpisują pod Twoim adresem, a sign.yt pozostaje w tle.\n\nKażdy podpisany dokument ma kod QR i krótki link, np. verify.sign.yt/k3Fq9ZxT2mPa7LwB, prowadzący do publicznej strony weryfikacji. W płatnym dodatku Pełna personalizacja te linki również działają w Twojej domenie.\n\nBranding jest ustalany w jednym miejscu, od zespołu przez organizację po ustawienia platformy, więc e-maile, strony podpisu i strony weryfikacji wyglądają spójnie, bez ręcznego kopiowania ustawień między zespołami.',
          },
        ],
        faq: [
          {
            q: 'Co mogę dostosować?',
            a: 'Logo, nazwę marki, kolor główny, motyw strony, nazwę nadawcy e-maili oraz stopkę wiadomości. Strona logowania przyjmuje też favicon, kolor dodatkowy i tekst powitalny.',
          },
          {
            q: 'Jakie motywy strony mogę wybrać?',
            a: 'Cztery: klasyczny, minimalny, ciemny i wyrazisty. Motyw obowiązuje na stronie podpisu i na publicznej stronie weryfikacji.',
          },
          {
            q: 'Czy każdy zespół może mieć własny branding?',
            a: 'Tak. Zespół może nadpisać logo, kolory, motyw i nazwę nadawcy. Jego dokumenty mają ten wygląd na stronie podpisu, stronie weryfikacji i w e-mailach, chyba że administrator zablokuje branding zespołów dla całej organizacji.',
          },
          {
            q: 'Czy branding obejmuje też e-maile?',
            a: 'Tak. Powiadomienia e-mail korzystają z brandingu organizacji albo nadpisania zespołu, jeśli istnieje, łącznie z nazwą nadawcy i stopką.',
          },
          {
            q: 'Gdzie pojawia się "Powered by sign.yt"?',
            a: 'Tylko w planie Starter, na stronie logowania i stronach organizacji. Plany od Professional wzwyż go nie pokazują.',
          },
          {
            q: 'Dokąd prowadzą linki weryfikacyjne?',
            a: 'Domyślnie do verify.sign.yt, krótkiego adresu, który otwiera stronę weryfikacji dokumentu w Twoim brandingu. Linki weryfikacyjne we własnej domenie to część płatnego dodatku Pełna personalizacja.',
          },
        ],
      },
    },
  },
  {
    slug: 'pades-electronic-signature',
    icon: 'FileSignature',
    group: 'trust',
    copy: {
      en: {
        name: 'PAdES signatures',
        title: 'PAdES electronic signatures on every PDF',
        desc: 'Every signer gets a cryptographic PAdES signature in the PDF, plus a final platform seal and a certificate of completion. Any change after signing shows.',
        h1: 'A cryptographic seal, not just a picture of a signature',
        lead: 'sign.yt does more than paste a drawn signature onto a page. Each signature is embedded in the PDF as a PAdES signature, so any later edit to the file is detectable.',
        sections: [
          {
            h2: 'One PAdES signature per signer',
            body: 'When a recipient finishes, sign.yt applies an incremental PAdES signature with a certificate issued for that signer by the platform certificate authority. Signatures are added one after another, so earlier ones stay valid.\n\nNew keys use ECDSA P-384 with SHA-384. When all parties have signed, a final platform seal closes the document.\n\nEach signer has their own entry in the PDF signature panel, with their name and signing time, instead of one anonymous stamp for the whole document.',
          },
          {
            h2: 'Trusted time and long-term validation',
            body: 'sign.yt can add an RFC 3161 timestamp from a timestamping authority to each signature. With a timestamp configured, the final seal is extended with validation data and a document timestamp (PAdES-B-LTA), so the signature can still be checked years from now.\n\nWithout a timestamping authority the signatures are produced at the baseline PAdES-B-B level. Either way, the signature covers every byte of the document as it looked at that moment.',
          },
          {
            h2: 'Verifiable outside sign.yt',
            body: 'The signed file is a standard PDF. Adobe Acrobat Reader and other PDF readers with signature support list the signatures and flag any modification made after signing.\n\nAnyone can also drop the PDF into the public sign.yt verifier, no account needed, to see who signed, when, and whether the bytes are intact.\n\nThe verifier checks whether the signature covers the whole file and whether the document carries the sign.yt platform seal. Where the EU DSS validator is enabled, it also shows an eIDAS verdict for each signature.\n\nEvery completed document gets a QR code and a short verify.sign.yt address on its certificate of completion. It opens a public page with masked signer names, signing dates, the final hash, the seal and the Bitcoin anchor status, and lets anyone check that their copy of the file is identical.',
          },
          {
            h2: 'Evidence attached to the document',
            body: 'A certificate of completion is appended to the signed PDF. For every signer it lists the email, verification method, timestamps of sending, viewing and signing, IP address and user agent, plus the consent statement they ticked before signing. The evidence travels with the file, not only in our database.\n\nThe hash of the final PDF is also anchored in Bitcoin through OpenTimestamps, and the whole package can be downloaded as one evidence ZIP.\n\nIf a signature is ever questioned, you have everything in one file that you can hand to a lawyer, an auditor or a court.',
          },
        ],
        faq: [
          {
            q: 'Is a sign.yt signature legally binding?',
            a: 'Under eIDAS an electronic signature cannot be denied legal effect just because it is electronic. Where the law requires written form, use a qualified electronic signature from a qualified trust service provider. Profil Zaufany and e-dowód signatures through Węzeł Podpisu are also available where enabled.',
          },
          {
            q: 'Will Adobe Reader show the signatures?',
            a: 'Yes. The signatures are standard PAdES and appear in the signature panel. Whether Adobe marks the signer identity as trusted depends on its own trust list.',
          },
          {
            q: 'What happens if someone edits the PDF after signing?',
            a: 'The edit falls outside the signed byte range, so PDF readers and the sign.yt verifier report the document as modified.',
          },
          {
            q: 'Do I need my own certificate?',
            a: 'No. sign.yt issues per-signer certificates automatically. Qualified signatures are available as a separate signing method.',
          },
        ],
      },
      pl: {
        name: 'Podpis PAdES',
        title: 'Podpis elektroniczny PAdES w każdym PDF',
        desc: 'Każdy podpisujący dostaje kryptograficzny podpis PAdES w pliku PDF, a dokument pieczęć platformy i certyfikat ukończenia. Każda zmiana jest widoczna.',
        h1: 'Kryptograficzna pieczęć, a nie tylko obrazek podpisu',
        lead: 'sign.yt nie ogranicza się do wklejenia odręcznego podpisu na stronę. Każdy podpis jest osadzony w pliku jako podpis PAdES, więc każda późniejsza zmiana w dokumencie jest wykrywalna.',
        sections: [
          {
            h2: 'Osobny podpis PAdES dla każdego podpisującego',
            body: 'Gdy odbiorca kończy podpisywanie, sign.yt składa przyrostowy podpis PAdES z certyfikatem wystawionym dla tej osoby przez urząd certyfikacji platformy. Kolejne podpisy są dokładane jeden po drugim, więc wcześniejsze pozostają ważne.\n\nNowe klucze to ECDSA P-384 z SHA-384. Gdy wszyscy podpiszą, dokument zamyka końcowa pieczęć platformy.\n\nKażdy podpisujący ma osobny wpis w panelu podpisów PDF, ze swoim imieniem i nazwiskiem oraz czasem podpisu, zamiast jednej anonimowej pieczątki dla całego dokumentu.',
          },
          {
            h2: 'Zaufany czas i długoterminowa weryfikacja',
            body: 'sign.yt może dołączyć do każdego podpisu znacznik czasu RFC 3161 z urzędu znakowania czasem. Przy skonfigurowanym znaczniku czasu końcowa pieczęć jest rozszerzana o dane walidacyjne i znacznik czasu dokumentu (PAdES-B-LTA), dzięki czemu podpis da się sprawdzić także za wiele lat.\n\nBez urzędu znakowania czasem podpisy powstają na podstawowym poziomie PAdES-B-B. W obu przypadkach podpis obejmuje każdy bajt dokumentu w chwili podpisania.',
          },
          {
            h2: 'Weryfikacja poza sign.yt',
            body: 'Podpisany plik to zwykły PDF. Adobe Acrobat Reader i inne czytniki obsługujące podpisy pokazują listę podpisów i sygnalizują każdą zmianę wprowadzoną po podpisaniu.\n\nKażdy może też wrzucić PDF do publicznego weryfikatora sign.yt, bez zakładania konta, i sprawdzić, kto podpisał, kiedy i czy plik jest nienaruszony.\n\nWeryfikator sprawdza, czy podpis obejmuje cały plik i czy dokument ma pieczęć platformy sign.yt. Tam, gdzie włączony jest unijny walidator DSS, pokazuje też ocenę eIDAS dla każdego podpisu.\n\nKażdy ukończony dokument ma na certyfikacie ukończenia kod QR i krótki adres verify.sign.yt. Prowadzi on do publicznej strony z zamaskowanymi danymi podpisujących, datami podpisów, końcowym skrótem, pieczęcią i statusem kotwicy w Bitcoinie, gdzie każdy sprawdzi, czy jego kopia pliku jest identyczna.',
          },
          {
            h2: 'Dowody dołączone do dokumentu',
            body: 'Do podpisanego PDF dołączany jest certyfikat ukończenia. Dla każdego podpisującego zawiera e-mail, metodę weryfikacji, czas wysłania, otwarcia i podpisu, adres IP i przeglądarkę oraz treść zgody zaznaczonej przed podpisem. Dowody są w samym pliku, nie tylko w naszej bazie.\n\nSkrót końcowego PDF jest też zakotwiczany w Bitcoinie przez OpenTimestamps, a cały pakiet pobierzesz jako jeden plik ZIP z dowodami.\n\nJeśli podpis zostanie kiedyś zakwestionowany, wszystko masz w jednym pliku, który możesz przekazać prawnikowi, audytorowi albo sądowi. Certyfikat jest czytelny także dla osób bez wiedzy technicznej.',
          },
        ],
        faq: [
          {
            q: 'Czy podpis w sign.yt jest wiążący prawnie?',
            a: 'Zgodnie z eIDAS podpisowi elektronicznemu nie można odmówić skutków prawnych tylko dlatego, że ma postać elektroniczną. Gdy przepis wymaga formy pisemnej, użyj kwalifikowanego podpisu elektronicznego od kwalifikowanego dostawcy usług zaufania. Podpis zaufany (Profil Zaufany) i podpis osobisty (e-dowód) przez Węzeł Podpisu są też dostępne tam, gdzie zostały włączone.',
          },
          {
            q: 'Czy Adobe Reader pokaże podpisy?',
            a: 'Tak. To standardowe podpisy PAdES widoczne w panelu podpisów. To, czy Adobe oznaczy tożsamość podpisującego jako zaufaną, zależy od jego własnej listy zaufania.',
          },
          {
            q: 'Co się stanie, jeśli ktoś zmieni PDF po podpisaniu?',
            a: 'Zmiana wypada poza podpisany zakres bajtów, więc czytniki PDF i weryfikator sign.yt pokażą, że dokument został zmodyfikowany.',
          },
          {
            q: 'Czy potrzebuję własnego certyfikatu?',
            a: 'Nie. sign.yt automatycznie wystawia certyfikaty dla podpisujących. Podpis kwalifikowany jest dostępny jako osobna metoda podpisu.',
          },
        ],
      },
    },
  },
  {
    slug: 'trusted-timestamps',
    requires: 'trusted_timestamps',
    icon: 'Clock',
    group: 'trust',
    copy: {
      en: {
        name: 'Trusted timestamps',
        title: 'RFC 3161 timestamps with automatic TSA fallback',
        desc: 'Every signature gets an RFC 3161 timestamp from an independent authority. Certum first, then DigiCert, Sectigo and FreeTSA if one is down.',
        h1: 'Signing time confirmed by an independent authority',
        lead: 'The time on a sign.yt signature does not come from our server clock alone. A timestamping authority signs it too, so the date holds up when someone asks.',
        sections: [
          {
            h2: 'Four authorities, one signature',
            body: 'sign.yt asks Certum for the timestamp first. If it does not answer, the next authority in the list takes over: DigiCert, then Sectigo, then FreeTSA. One signature always uses one authority from start to finish, and a recovered primary is used again after a few minutes.\n\nIf every authority is unreachable, the document is still signed at the PAdES-B-B level instead of waiting forever.',
          },
          {
            h2: 'Long-term validation built in',
            body: 'With a timestamp in place, the final seal is extended with validation data and a document timestamp (PAdES-B-LTA). The roots of all four authorities ship with the platform, so the validation path can be built without extra setup.\n\nThat means the signature can still be checked years from now, after the signing certificates have expired.',
          },
          {
            h2: 'Qualification shown on the verification page',
            body: 'When you upload a signed PDF to the verifier, each timestamp is checked against the EU trusted lists. The page shows whether it is a signature or an archive timestamp, and whether it comes from a qualified timestamping authority (QTSA), whose date is legally presumed accurate.',
            requires: 'eidas_validation',
          },
        ],
        faq: [
          {
            q: 'What is an RFC 3161 timestamp?',
            a: 'A small token signed by a timestamping authority that proves a given hash existed at a given time. It is embedded in the PDF signature.',
          },
          {
            q: 'What happens if a timestamping authority is down?',
            a: 'sign.yt moves on to the next one in the list. Only if all of them fail is the signature created without a timestamp.',
          },
          {
            q: 'Can I use my own timestamping authority?',
            a: 'The authority list is set per platform. Contact us if your contracts require a specific qualified provider.',
          },
        ],
      },
      pl: {
        name: 'Znaczniki czasu',
        title: 'Znaczniki czasu RFC 3161 z automatycznym zapasowym TSA',
        desc: 'Każdy podpis dostaje znacznik czasu RFC 3161 od niezależnego urzędu. Najpierw Certum, a gdy nie odpowiada: DigiCert, Sectigo i FreeTSA.',
        h1: 'Czas podpisu potwierdzony przez niezależny urząd',
        lead: 'Czas na podpisie sign.yt nie pochodzi wyłącznie z zegara naszego serwera. Podpisuje go także urząd znacznika czasu, więc data obroni się, gdy ktoś o nią zapyta.',
        sections: [
          {
            h2: 'Cztery urzędy, jeden podpis',
            body: 'sign.yt najpierw prosi o znacznik czasu Certum. Jeśli nie odpowiada, przejmuje kolejny urząd z listy: DigiCert, potem Sectigo, potem FreeTSA. Jeden podpis zawsze korzysta z jednego urzędu od początku do końca, a główny urząd wraca do użycia po kilku minutach.\n\nGdy żaden urząd nie jest dostępny, dokument i tak zostaje podpisany na poziomie PAdES-B-B, zamiast czekać bez końca.',
          },
          {
            h2: 'Długoterminowa weryfikacja w standardzie',
            body: 'Ze znacznikiem czasu końcowa pieczęć jest rozszerzana o dane walidacyjne i znacznik czasu dokumentu (PAdES-B-LTA). Certyfikaty główne wszystkich czterech urzędów są dołączone do platformy, więc ścieżka walidacji buduje się bez dodatkowej konfiguracji.\n\nDzięki temu podpis da się sprawdzić także za kilka lat, gdy certyfikaty podpisujących już wygasną.',
          },
          {
            h2: 'Kwalifikacja widoczna przy weryfikacji',
            body: 'Gdy wgrasz podpisany PDF do weryfikatora, każdy znacznik czasu jest sprawdzany z unijnymi listami zaufanych usług. Strona pokazuje, czy to znacznik podpisu czy archiwalny i czy pochodzi od kwalifikowanego dostawcy (QTSA), którego data korzysta z domniemania prawdziwości.',
            requires: 'eidas_validation',
          },
        ],
        faq: [
          {
            q: 'Czym jest znacznik czasu RFC 3161?',
            a: 'To niewielki token podpisany przez urząd znacznika czasu, który dowodzi, że dany skrót istniał w określonej chwili. Jest osadzony w podpisie PDF.',
          },
          {
            q: 'Co, jeśli urząd znacznika czasu nie działa?',
            a: 'sign.yt przechodzi do kolejnego z listy. Dopiero gdy zawiodą wszystkie, podpis powstaje bez znacznika czasu.',
          },
          {
            q: 'Czy mogę użyć własnego urzędu znacznika czasu?',
            a: 'Lista urzędów jest ustawiana dla całej platformy. Napisz do nas, jeśli Twoje umowy wymagają konkretnego dostawcy kwalifikowanego.',
          },
        ],
      },
    },
  },
  {
    slug: 'bitcoin-anchoring',
    requires: 'bitcoin_anchoring',
    icon: 'Bitcoin',
    group: 'trust',
    wide: true,
    copy: {
      en: {
        name: 'Bitcoin anchoring',
        title: 'Document hashes anchored in the Bitcoin blockchain',
        desc: 'Every completed document is anchored in Bitcoin with OpenTimestamps. Anyone can check the proof with open-source tools, without trusting sign.yt.',
        h1: 'Proof that does not depend on us',
        lead: 'When a document is completed, its fingerprint goes into a Bitcoin block. Years later anyone can show the file existed at that moment, even if sign.yt is gone.',
        sections: [
          {
            h2: 'What gets anchored',
            body: 'sign.yt anchors the SHA-256 hashes of the signed PDF, of the audit trail and of the head of the audit chain through OpenTimestamps. Only hashes leave the platform, never the document or any personal data.\n\nThe proof is usually confirmed in a Bitcoin block within a few hours.',
          },
          {
            h2: 'Check it yourself',
            body: 'The evidence ZIP contains the .ots proof and the list of anchored hashes. Verify them with the open-source ots client, against any Bitcoin node or block explorer, without an account and without contacting us.\n\nThe public verification page also shows the anchor status and the block height, with a link to the block.',
          },
          {
            h2: 'Why a blockchain',
            body: 'A database row can be edited by whoever runs the database. A Bitcoin block cannot be changed by anyone. That makes the anchor a strong, independent answer to the question "did this file exist on that day, in exactly this form?".',
          },
        ],
        faq: [
          {
            q: 'Does sign.yt put my document on the blockchain?',
            a: 'No. Only hashes are anchored. A hash reveals nothing about the content, but any change to the file gives a different hash.',
          },
          {
            q: 'Do I pay for Bitcoin transactions?',
            a: 'No. Public OpenTimestamps calendars combine many hashes into one transaction, so there is no fee per document.',
          },
          {
            q: 'How long until the proof is final?',
            a: 'Usually a few hours. Until then the anchor shows as pending; the proof upgrades automatically.',
          },
        ],
      },
      pl: {
        name: 'Kotwica w Bitcoinie',
        title: 'Skróty dokumentów zakotwiczone w blockchainie Bitcoina',
        desc: 'Każdy ukończony dokument jest kotwiczony w Bitcoinie przez OpenTimestamps. Dowód sprawdzisz otwartymi narzędziami, bez zaufania do sign.yt.',
        h1: 'Dowód, który nie zależy od nas',
        lead: 'Po ukończeniu dokumentu jego odcisk trafia do bloku Bitcoina. Za kilka lat każdy pokaże, że plik istniał w tej chwili, nawet jeśli sign.yt już nie będzie.',
        sections: [
          {
            h2: 'Co jest kotwiczone',
            body: 'sign.yt kotwiczy przez OpenTimestamps skróty SHA-256 podpisanego PDF, ścieżki audytu i czoła łańcucha audytu. Platformę opuszczają tylko skróty, nigdy dokument ani dane osobowe.\n\nDowód zwykle zostaje potwierdzony w bloku Bitcoina w ciągu kilku godzin.',
          },
          {
            h2: 'Sprawdź sam',
            body: 'Plik ZIP z dowodami zawiera dowód .ots i listę zakotwiczonych skrótów. Zweryfikujesz je otwartym klientem ots, z dowolnym węzłem Bitcoina lub eksploratorem bloków, bez konta i bez kontaktu z nami.\n\nPubliczna strona weryfikacji pokazuje też status kotwicy i numer bloku, z linkiem do bloku.',
          },
          {
            h2: 'Dlaczego blockchain',
            body: 'Wiersz w bazie danych może zmienić ten, kto tą bazą zarządza. Bloku Bitcoina nie zmieni nikt. Dlatego kotwica to mocna i niezależna odpowiedź na pytanie: czy ten plik istniał tego dnia, dokładnie w tej postaci?',
          },
        ],
        faq: [
          {
            q: 'Czy sign.yt umieszcza mój dokument w blockchainie?',
            a: 'Nie. Kotwiczone są tylko skróty. Skrót nie ujawnia treści, ale każda zmiana pliku daje inny skrót.',
          },
          {
            q: 'Czy płacę za transakcje w Bitcoinie?',
            a: 'Nie. Publiczne kalendarze OpenTimestamps łączą wiele skrótów w jedną transakcję, więc nie ma opłaty za dokument.',
          },
          {
            q: 'Ile trwa, zanim dowód jest ostateczny?',
            a: 'Zwykle kilka godzin. Do tego czasu kotwica ma status oczekującej, a dowód aktualizuje się automatycznie.',
          },
        ],
      },
    },
  },
  {
    slug: 'post-quantum-seal',
    requires: 'pq_seal',
    icon: 'Atom',
    group: 'trust',
    copy: {
      en: {
        name: 'Post-quantum seal',
        title: 'Post-quantum evidence seal: ML-DSA-65 + Ed25519',
        desc: 'A second seal over every completed document, signed with ML-DSA-65 and Ed25519. Built to stay verifiable after quantum computers break today\'s keys.',
        h1: 'Evidence that survives the quantum era',
        lead: 'Today\'s signatures rely on maths that a large quantum computer could break. sign.yt adds a hybrid post-quantum seal, so your evidence keeps its value for decades.',
        sections: [
          {
            h2: 'Two algorithms, both must hold',
            body: 'The seal signs a manifest that binds the hash of the final PDF, the head of the audit chain and the signer evidence. It is signed twice: with ML-DSA-65 (FIPS 204, post-quantum) and with Ed25519. The seal is valid only if both signatures verify, so it holds as long as either algorithm stays unbroken.\n\nIt is supplementary evidence next to the PAdES signature, not a replacement for a qualified signature.',
          },
          {
            h2: 'Verifiable offline',
            body: 'The seal file is part of the evidence ZIP. Upload it with the PDF to the public verifier, or check it offline against the published public keys.\n\nAn offline root key certifies or revokes the working keys. Its private half never touches a server. A leaked working key can be revoked, and seals from revoked or uncertified keys fail verification.',
          },
          {
            h2: 'Keys inside AWS KMS',
            body: 'On this platform the seal keys live in AWS Key Management Service. Signing happens inside the HSM and the private keys never leave it; each signature is checked locally before it is stored.',
            requires: 'aws_kms',
          },
        ],
        faq: [
          {
            q: 'Why do I need this today?',
            a: 'Contracts are kept for years. An attacker can store signed files now and try to forge them once quantum computers are strong enough. The seal closes that gap.',
          },
          {
            q: 'Does it replace the PAdES signature?',
            a: 'No. PAdES stays the standard signature that PDF readers understand. The post-quantum seal is an extra, independent layer of evidence.',
          },
          {
            q: 'What is ML-DSA?',
            a: 'A digital signature algorithm standardised by NIST in FIPS 204, designed to resist attacks by quantum computers.',
          },
        ],
      },
      pl: {
        name: 'Pieczęć postkwantowa',
        title: 'Postkwantowa pieczęć dowodowa: ML-DSA-65 + Ed25519',
        desc: 'Druga pieczęć na każdym ukończonym dokumencie, podpisana ML-DSA-65 i Ed25519. Odporna na komputery kwantowe, które złamią dzisiejsze klucze.',
        h1: 'Dowody, które przetrwają erę kwantową',
        lead: 'Dzisiejsze podpisy opierają się na matematyce, którą duży komputer kwantowy mógłby złamać. sign.yt dodaje hybrydową pieczęć postkwantową, więc Twoje dowody zachowają wartość przez dekady.',
        sections: [
          {
            h2: 'Dwa algorytmy, oba muszą się zgadzać',
            body: 'Pieczęć podpisuje manifest wiążący skrót końcowego PDF, czoło łańcucha audytu i dowody podpisujących. Jest podpisana dwa razy: algorytmem ML-DSA-65 (FIPS 204, postkwantowy) i Ed25519. Pieczęć jest ważna tylko wtedy, gdy oba podpisy się weryfikują, więc trzyma, dopóki choć jeden algorytm pozostaje niezłamany.\n\nTo dodatkowy dowód obok podpisu PAdES, a nie zamiennik podpisu kwalifikowanego.',
          },
          {
            h2: 'Weryfikacja offline',
            body: 'Plik pieczęci jest częścią ZIP z dowodami. Wgrywasz go razem z PDF do publicznego weryfikatora albo sprawdzasz offline z opublikowanymi kluczami publicznymi.\n\nKlucz główny offline certyfikuje lub unieważnia klucze robocze. Jego część prywatna nigdy nie trafia na serwer. Wyciekły klucz roboczy można unieważnić, a pieczęcie z kluczy unieważnionych lub niecertyfikowanych nie przechodzą weryfikacji.',
          },
          {
            h2: 'Klucze w AWS KMS',
            body: 'Na tej platformie klucze pieczęci są przechowywane w AWS Key Management Service. Podpis powstaje wewnątrz HSM, a klucze prywatne nigdy go nie opuszczają; każdy podpis jest sprawdzany lokalnie przed zapisaniem.',
            requires: 'aws_kms',
          },
        ],
        faq: [
          {
            q: 'Po co mi to już dziś?',
            a: 'Umowy przechowuje się latami. Atakujący może zebrać podpisane pliki teraz i próbować je podrobić, gdy komputery kwantowe będą wystarczająco silne. Pieczęć zamyka tę lukę.',
          },
          {
            q: 'Czy zastępuje podpis PAdES?',
            a: 'Nie. PAdES pozostaje standardowym podpisem, który rozumieją czytniki PDF. Pieczęć postkwantowa to dodatkowa, niezależna warstwa dowodów.',
          },
          {
            q: 'Czym jest ML-DSA?',
            a: 'To algorytm podpisu cyfrowego ustandaryzowany przez NIST w FIPS 204, zaprojektowany tak, by opierał się atakom komputerów kwantowych.',
          },
        ],
      },
    },
  },
  {
    slug: 'public-verification',
    icon: 'QrCode',
    group: 'trust',
    copy: {
      en: {
        name: 'Public verification',
        title: 'Verify a signed document by QR code or short link',
        desc: 'Each signed document carries a QR code and short link to a public check page. Masked names, signing dates, seal status and a file comparison.',
        h1: 'Anyone can check a document in seconds',
        lead: 'A counterparty, a bank or a court does not need an account to trust your document. They scan the QR code, or drop the PDF into the verifier.',
        sections: [
          {
            h2: 'A QR code on every certificate',
            body: 'The certificate of completion of each signed document has a QR code and a short link such as verify.sign.yt/k3Fq9ZxT2mPa7LwB. It opens a public page showing the document status, how many signatures were collected and when, the hash of the signed original, the platform seal, the post-quantum seal and the Bitcoin anchor.\n\nThe page uses the branding of the organization that sent the document.',
          },
          {
            h2: 'Check that your file is identical',
            body: 'The QR code proves the document exists in sign.yt. To prove your copy is unchanged, upload it on the same page: its hash is compared with the hash of the signed original, and the full signature report is shown next to it.',
          },
          {
            h2: 'No personal data in public',
            body: 'Signer names are masked, for example "J. K*****", and no emails, phone numbers or IP addresses are shown. The link is a long random code that cannot be guessed, and lookups are rate-limited.\n\nAny PDF, signed with sign.yt or not, can also be dropped into the verifier to see whether its signatures are intact.',
          },
        ],
        faq: [
          {
            q: 'Does the person checking need an account?',
            a: 'No. Both the QR page and the PDF verifier are public.',
          },
          {
            q: 'What does the public page reveal?',
            a: 'Status, masked signer names with signing dates, the final hash and the seal and anchor status. Not the content of the document.',
          },
          {
            q: 'Can the links use my own domain?',
            a: 'By default they point to verify.sign.yt. Moving them to your domain is part of the paid Full customization add-on.',
          },
        ],
      },
      pl: {
        name: 'Publiczna weryfikacja',
        title: 'Weryfikacja dokumentu kodem QR lub linkiem',
        desc: 'Każdy podpisany dokument ma kod QR i krótki link do publicznej strony weryfikacji. Zamaskowane nazwiska, daty podpisów, status pieczęci i porównanie pliku.',
        h1: 'Każdy sprawdzi dokument w kilka sekund',
        lead: 'Kontrahent, bank czy sąd nie potrzebuje konta, żeby zaufać Twojemu dokumentowi. Skanuje kod QR albo wrzuca PDF do weryfikatora.',
        sections: [
          {
            h2: 'Kod QR na każdym certyfikacie',
            body: 'Certyfikat ukończenia każdego podpisanego dokumentu ma kod QR i krótki link, np. verify.sign.yt/k3Fq9ZxT2mPa7LwB. Otwiera on publiczną stronę ze statusem dokumentu, liczbą zebranych podpisów i ich datami, skrótem podpisanego oryginału, pieczęcią platformy, pieczęcią postkwantową i kotwicą w Bitcoinie.\n\nStrona ma branding organizacji, która wysłała dokument.',
          },
          {
            h2: 'Sprawdź, czy Twój plik jest identyczny',
            body: 'Kod QR dowodzi, że dokument istnieje w sign.yt. Żeby wykazać, że Twoja kopia jest niezmieniona, wgrywasz ją na tej samej stronie: jej skrót jest porównywany ze skrótem podpisanego oryginału, a obok pojawia się pełny raport podpisów.',
          },
          {
            h2: 'Bez danych osobowych na widoku',
            body: 'Nazwiska podpisujących są zamaskowane, np. "J. K*****", a e-maile, numery telefonów i adresy IP nie są pokazywane. Link to długi losowy kod, którego nie da się zgadnąć, a liczba zapytań jest ograniczona.\n\nDo weryfikatora możesz też wrzucić dowolny PDF, podpisany w sign.yt lub nie, i sprawdzić, czy jego podpisy są nienaruszone.',
          },
        ],
        faq: [
          {
            q: 'Czy osoba sprawdzająca potrzebuje konta?',
            a: 'Nie. Zarówno strona z kodu QR, jak i weryfikator PDF są publiczne.',
          },
          {
            q: 'Co pokazuje strona publiczna?',
            a: 'Status, zamaskowane nazwiska z datami podpisów, skrót końcowy oraz status pieczęci i kotwicy. Nie pokazuje treści dokumentu.',
          },
          {
            q: 'Czy linki mogą działać w mojej domenie?',
            a: 'Domyślnie prowadzą do verify.sign.yt. Przeniesienie ich do Twojej domeny to część płatnego dodatku Pełna personalizacja.',
          },
        ],
      },
    },
  },
  {
    slug: 'sms-email-verification',
    icon: 'MessageSquare',
    group: 'docs',
    copy: {
      en: {
        name: 'SMS and email verification',
        title: 'SMS and email code verification for signers',
        desc: 'Confirm who is signing with a one-time code sent by email, by SMS, or both. Codes expire after 10 minutes and every check lands in the audit trail.',
        h1: 'Know who signed, not just which link was clicked',
        lead: 'Add a one-time code step before signing. The recipient proves access to their mailbox, their phone, or both, and the result is recorded as evidence.',
        sections: [
          {
            h2: 'Choose the level per document',
            body: 'For each document you decide how strict verification should be: none for a simple acknowledgement, an email code for everyday contracts, an SMS code for higher-value agreements, or both. With both, the recipient confirms the email code when opening the document and the SMS code right before signing.\n\nThe recipient never needs an account or an app. The code arrives where they already are, and the signing page guides them through the step.',
          },
          {
            h2: 'Six digits, short-lived, hard to guess',
            body: 'Codes are six random digits valid for 10 minutes. After five wrong attempts a code is burned, a new one can be requested after 60 seconds, and verify attempts per link are capped per day.\n\nSMS messages also have a per-number hourly limit, which protects your account against SMS pumping.\n\nThese limits apply automatically, there is nothing to configure.',
          },
          {
            h2: 'SMS through BulkGate',
            body: 'Text messages go out through BulkGate, a European SMS gateway. You can set your own sender name of up to 11 characters, so the code arrives from your brand rather than a random number.\n\nPhone numbers are stored in international E.164 format. If a message is slow to arrive, the recipient can request a new code from the signing page after the short cooldown, without contacting you.',
          },
          {
            h2: 'Proof in the certificate of completion',
            body: 'The verification method and the exact time each code was confirmed appear in the certificate of completion attached to the signed PDF and in the audit log. If a signature is ever disputed, you can show which channel the signer controlled.\n\nSigners who used a code are clearly distinguished from those who signed without one.',
          },
        ],
        faq: [
          {
            q: 'How long is a code valid?',
            a: '10 minutes. After that, the recipient requests a new one from the signing page.',
          },
          {
            q: 'Does the recipient need an account?',
            a: 'No. Recipients sign from the link in their email; the code is the only extra step.',
          },
          {
            q: 'Can I require both email and SMS?',
            a: 'Yes. The email code is checked when the document opens and the SMS code right before signing.',
          },
          {
            q: 'Is there stronger verification than codes?',
            a: 'Yes. Identity verification through login.gov.pl, Profil Zaufany or qualified signatures is available where enabled for your organization.',
          },
        ],
      },
      pl: {
        name: 'Weryfikacja SMS i e-mail',
        title: 'Weryfikacja kodem SMS i e-mail przed podpisem',
        desc: 'Potwierdź, kto podpisuje, jednorazowym kodem wysłanym e-mailem, SMS-em lub oboma kanałami. Kod wygasa po 10 minutach, a weryfikacja trafia do audytu.',
        h1: 'Wiesz, kto podpisał, a nie tylko kto kliknął w link',
        lead: 'Dodaj krok z jednorazowym kodem przed podpisem. Odbiorca potwierdza dostęp do skrzynki, telefonu albo obu, a wynik zostaje zapisany jako dowód.',
        sections: [
          {
            h2: 'Poziom weryfikacji dla każdego dokumentu',
            body: 'Dla każdego dokumentu decydujesz, jak ścisła ma być weryfikacja: bez kodu przy prostym potwierdzeniu, kod e-mail przy codziennych umowach, kod SMS przy ważniejszych umowach albo oba naraz. Przy obu kanałach odbiorca wpisuje kod e-mail przy otwarciu dokumentu, a kod SMS tuż przed podpisem.\n\nOdbiorca nie potrzebuje konta ani aplikacji. Kod trafia tam, gdzie już jest, a strona podpisu prowadzi go przez ten krok.',
          },
          {
            h2: 'Sześć cyfr, krótka ważność, trudne do zgadnięcia',
            body: 'Kod to sześć losowych cyfr ważnych przez 10 minut. Po pięciu błędnych próbach kod jest unieważniany, nowy można wysłać po 60 sekundach, a liczba prób na link jest ograniczona dziennie.\n\nSMS-y mają dodatkowo godzinny limit na numer, co chroni Twoje konto przed nadużyciami typu SMS pumping.\n\nTe limity działają automatycznie, nie trzeba niczego konfigurować. Odbiorca widzi jasny komunikat, gdy kod wygaśnie.',
          },
          {
            h2: 'SMS przez BulkGate',
            body: 'Wiadomości SMS wysyłamy przez BulkGate, europejską bramkę SMS. Możesz ustawić własną nazwę nadawcy do 11 znaków, więc kod przychodzi od Twojej marki, a nie z przypadkowego numeru.\n\nNumery telefonów zapisujemy w międzynarodowym formacie E.164. Jeśli wiadomość się spóźnia, odbiorca po krótkiej przerwie sam poprosi o nowy kod na stronie podpisu, bez kontaktu z Tobą. Nazwa nadawcy pomaga też odróżnić Twój SMS od prób oszustwa.',
          },
          {
            h2: 'Dowód w certyfikacie ukończenia',
            body: 'Metoda weryfikacji i dokładny czas potwierdzenia każdego kodu trafiają do certyfikatu ukończenia dołączonego do podpisanego PDF oraz do dziennika audytu. Jeśli podpis zostanie kiedyś zakwestionowany, pokażesz, który kanał kontrolował podpisujący.\n\nOd razu widać, którzy podpisujący potwierdzili tożsamość kodem, a którzy podpisali bez niego. To ważne przy umowach, do których wraca się po latach. Dowód jest zapisany w samym pliku PDF.',
          },
        ],
        faq: [
          {
            q: 'Jak długo ważny jest kod?',
            a: '10 minut. Potem odbiorca może poprosić o nowy kod na stronie podpisu.',
          },
          {
            q: 'Czy odbiorca musi mieć konto?',
            a: 'Nie. Odbiorca podpisuje z linku w e-mailu, a kod to jedyny dodatkowy krok.',
          },
          {
            q: 'Czy mogę wymagać kodu e-mail i SMS jednocześnie?',
            a: 'Tak. Kod e-mail jest sprawdzany przy otwarciu dokumentu, a kod SMS tuż przed podpisem.',
          },
          {
            q: 'Czy jest coś mocniejszego niż kody?',
            a: 'Tak. Weryfikacja tożsamości przez login.gov.pl, Profil Zaufany lub podpis kwalifikowany jest dostępna, jeśli została włączona dla Twojej organizacji.',
          },
        ],
      },
    },
  },
  {
    slug: 'mobile-signing',
    icon: 'Smartphone',
    group: 'docs',
    wide: true,
    copy: {
      en: {
        name: 'Mobile signing',
        title: 'Sign on a phone, from a personal invite',
        desc: 'Recipients sign on their phone in a bottom sheet with a sticky action bar. Invites come from a named sender, with your own subject and message.',
        h1: 'Most signers open the link on their phone',
        lead: 'So the signing page is built for one thumb. And the email that brings them there comes from a real person, not from a no-reply robot.',
        sections: [
          {
            h2: 'A signing flow made for small screens',
            body: 'On a phone the PDF flows with the page, rendered sharp for the screen and re-fitted when the phone rotates. Tap a field, or the sticky bar at the bottom, and the signing controls open as a bottom sheet: draw or type the signature, fill the field, move on.\n\nThe bar always shows what is left to do, and it disappears when a document is expired or cancelled.',
          },
          {
            h2: 'An invite from a person',
            body: 'The invite shows the sender by name, for example "Anna Nowak via sign.yt" or via your own brand name, and replies go straight to Anna instead of a no-reply box. The default subject says who asks for a signature and for which document.\n\nBefore sending you can change the subject and add a personal message. It works with the managed sender and with every email provider you connect.',
          },
          {
            h2: 'Fewer "is this phishing?" questions',
            body: 'A familiar name, your brand and a message written for this one recipient make the email easy to trust. Recipients who have a question just press reply.',
          },
        ],
        faq: [
          {
            q: 'Does the signer need an app?',
            a: 'No. Signing works in the phone\'s browser from the link in the email.',
          },
          {
            q: 'Where do replies to the invite go?',
            a: 'To the person who sent the document. The Reply-To header is set to the sender.',
          },
          {
            q: 'Can I write my own email text?',
            a: 'Yes. Edit the subject and add a personal message in the send dialog.',
          },
        ],
      },
      pl: {
        name: 'Podpis na telefonie',
        title: 'Podpis na telefonie, z osobistego zaproszenia',
        desc: 'Odbiorcy podpisują na telefonie w dolnym panelu z przypiętym paskiem akcji. Zaproszenie przychodzi od konkretnej osoby, z Twoim tematem i wiadomością.',
        h1: 'Większość osób otwiera link na telefonie',
        lead: 'Dlatego strona podpisu jest zaprojektowana pod jeden kciuk. A e-mail, który tam prowadzi, wysyła prawdziwa osoba, a nie automat no-reply.',
        sections: [
          {
            h2: 'Podpisywanie zaprojektowane na mały ekran',
            body: 'Na telefonie PDF przewija się razem ze stroną, ostry na każdym ekranie i dopasowany po obróceniu telefonu. Stukasz w pole albo w przypięty pasek na dole, a narzędzia podpisu otwierają się jako dolny panel: rysujesz lub wpisujesz podpis, wypełniasz pole, idziesz dalej.\n\nPasek zawsze pokazuje, co zostało do zrobienia, i znika, gdy dokument wygasł lub został anulowany.',
          },
          {
            h2: 'Zaproszenie od człowieka',
            body: 'Zaproszenie pokazuje nadawcę z imienia i nazwiska, np. "Anna Nowak via sign.yt" albo z nazwą Twojej marki, a odpowiedzi trafiają prosto do Anny, a nie do skrzynki no-reply. Domyślny temat mówi, kto prosi o podpis i pod jakim dokumentem.\n\nPrzed wysyłką możesz zmienić temat i dodać osobistą wiadomość. Działa to z nadawcą zarządzanym i z każdym podłączonym dostawcą poczty.',
          },
          {
            h2: 'Mniej pytań "czy to phishing?"',
            body: 'Znane nazwisko, Twoja marka i wiadomość napisana do tej jednej osoby sprawiają, że e-mailowi łatwo zaufać. Gdy odbiorca ma pytanie, po prostu klika "odpowiedz".',
          },
        ],
        faq: [
          {
            q: 'Czy podpisujący potrzebuje aplikacji?',
            a: 'Nie. Podpis działa w przeglądarce telefonu, z linku w e-mailu.',
          },
          {
            q: 'Dokąd trafiają odpowiedzi na zaproszenie?',
            a: 'Do osoby, która wysłała dokument. Nagłówek Reply-To wskazuje nadawcę.',
          },
          {
            q: 'Czy mogę napisać własny tekst e-maila?',
            a: 'Tak. W oknie wysyłki edytujesz temat i dodajesz osobistą wiadomość.',
          },
        ],
      },
    },
  },
  {
    slug: 'profil-zaufany-wezel-podpisu',
    icon: 'Fingerprint',
    group: 'trust',
    copy: {
      en: {
        name: 'Profil Zaufany and qualified signatures',
        title: 'Profil Zaufany, e-ID and qualified e-signatures',
        desc: 'Let Polish signers use Profil Zaufany, e-dowód or mObywatel via Węzeł Podpisu, or sign with a qualified electronic signature from a trust service provider.',
        h1: 'Government-grade identity for the documents that need it',
        lead: 'Some contracts need more than a code. sign.yt connects to Węzeł Podpisu and to qualified trust service providers, so signers can use the identity tools they already have.',
        sections: [
          {
            h2: 'Węzeł Podpisu inside the signing flow',
            body: 'For each recipient you can pick a Węzeł Podpisu method: Profil Zaufany, e-dowód, personal signature (podpis osobisty), mObywatel or a qualified signature. The recipient is redirected to the government flow, signs there, and sign.yt stores the signed PDF and moves the document to the next signer.\n\nParallel and sequential signing orders work the same way as with regular signatures.',
          },
          {
            h2: 'Qualified electronic signatures',
            body: 'A qualified signature (QES) is the eIDAS level equal to a handwritten signature. sign.yt supports two paths: remote signing at a qualified provider through the Cloud Signature Consortium API, or upload, where the signer signs the PDF with any QES tool and uploads it back.\n\nRemote signing works with providers that expose a CSC v2 endpoint. The upload path works with smart cards, cloud certificates and other qualified tools, so the signer is not tied to one vendor.',
          },
          {
            h2: 'Earlier signatures stay intact',
            body: 'Qualified signatures are added incrementally to the latest revision of the document. Uploaded files are accepted only if they extend the exact bytes sign.yt sent, and the signing certificate must be listed as qualified on the EU trusted list.\n\nThat means you can combine methods on one document: one party signs with Profil Zaufany, another with a qualified certificate, and a third with a regular sign.yt signature, while the audit trail records each step.',
          },
        ],
        faq: [
          {
            q: 'Do I need a qualified signature?',
            a: 'Only where the law requires written form or your counterparty asks for it. For most commercial agreements a regular electronic signature with verification is enough. Check with your lawyer for specific cases.',
          },
          {
            q: 'Does the signer pay anything?',
            a: 'Profil Zaufany, e-dowód and mObywatel are the signer\'s own government tools. Qualified certificates are issued by trust service providers on their own terms.',
          },
          {
            q: 'Is this available on every account?',
            a: 'Węzeł Podpisu and qualified provider connections are enabled per organization. Contact sales@sign.yt to set them up.',
          },
          {
            q: 'Which qualified tools work with the upload path?',
            a: 'Any tool that produces a PAdES qualified signature recognised on the EU trusted list, for example a smart card or a cloud certificate.',
          },
        ],
      },
      pl: {
        name: 'Profil Zaufany i podpis kwalifikowany',
        title: 'Podpis Profilem Zaufanym i e-dowodem online',
        desc: 'Podpis Profilem Zaufanym, e-dowodem lub w mObywatelu przez Węzeł Podpisu albo kwalifikowanym podpisem elektronicznym. Wszystko w jednym obiegu dokumentu.',
        h1: 'Podpis Profilem Zaufanym tam, gdzie liczy się tożsamość',
        lead: 'Niektóre umowy wymagają czegoś więcej niż kod SMS. sign.yt łączy się z Węzłem Podpisu i z dostawcami usług kwalifikowanych, więc podpisujący korzystają z narzędzi, które już mają.',
        sections: [
          {
            h2: 'Węzeł Podpisu w obiegu dokumentu',
            body: 'Dla każdego odbiorcy wybierasz metodę z Węzła Podpisu: Profil Zaufany, e-dowód, podpis osobisty, mObywatel albo podpis kwalifikowany. Odbiorca przechodzi do rządowego procesu, podpisuje tam dokument, a sign.yt zapisuje podpisany PDF i przekazuje go kolejnej osobie.\n\nKolejność równoległa i sekwencyjna działa tak samo jak przy zwykłych podpisach. Odbiorca nie zakłada konta w sign.yt, korzysta z logowania, które już zna. Metodę wybierasz osobno dla każdego odbiorcy.',
          },
          {
            h2: 'Kwalifikowany podpis elektroniczny',
            body: 'Podpis kwalifikowany to poziom eIDAS równoważny podpisowi własnoręcznemu. sign.yt obsługuje dwie ścieżki: zdalny podpis u kwalifikowanego dostawcy przez API Cloud Signature Consortium albo wgranie pliku, gdy podpisujący podpisze PDF dowolnym narzędziem kwalifikowanym i odeśle go do platformy.\n\nZdalny podpis działa z dostawcami udostępniającymi interfejs CSC v2. Ścieżka z wgraniem pliku działa z kartą kryptograficzną, certyfikatem w chmurze i innymi narzędziami kwalifikowanymi, więc podpisujący nie jest przywiązany do jednego dostawcy.',
          },
          {
            h2: 'Wcześniejsze podpisy pozostają nienaruszone',
            body: 'Podpisy kwalifikowane są dokładane przyrostowo do najnowszej wersji dokumentu. Wgrany plik akceptujemy tylko wtedy, gdy rozszerza dokładnie te bajty, które wysłał sign.yt, a certyfikat podpisu figuruje jako kwalifikowany na unijnej liście zaufanych dostawców.\n\nDzięki temu w jednym dokumencie możesz łączyć metody: jedna strona podpisuje Profilem Zaufanym, druga certyfikatem kwalifikowanym, a trzecia zwykłym podpisem sign.yt, a dziennik audytu zapisuje każdy krok.',
          },
        ],
        faq: [
          {
            q: 'Czy potrzebuję podpisu kwalifikowanego?',
            a: 'Tylko gdy przepis wymaga formy pisemnej albo druga strona tego oczekuje. Dla większości umów handlowych wystarczy zwykły podpis elektroniczny z weryfikacją. W konkretnych sprawach skonsultuj się z prawnikiem.',
          },
          {
            q: 'Czy podpisujący coś płaci?',
            a: 'Profil Zaufany, e-dowód i mObywatel to bezpłatne narzędzia państwowe podpisującego. Certyfikaty kwalifikowane wydają dostawcy usług zaufania na własnych zasadach.',
          },
          {
            q: 'Czy to działa na każdym koncie?',
            a: 'Węzeł Podpisu i połączenia z dostawcami kwalifikowanymi włączamy dla organizacji. Napisz na sales@sign.yt, aby je skonfigurować.',
          },
          {
            q: 'Jakie narzędzia kwalifikowane działają przy wgrywaniu pliku?',
            a: 'Każde, które tworzy kwalifikowany podpis PAdES uznawany na unijnej liście zaufania, np. karta kryptograficzna albo certyfikat w chmurze.',
          },
        ],
      },
    },
  },
  {
    slug: 'email-delivery-providers',
    icon: 'Mail',
    group: 'brand',
    copy: {
      en: {
        name: 'Email delivery',
        title: 'Send signing emails from your own domain',
        desc: 'Send signing invitations through your own SMTP, Microsoft 365 (Graph API), Resend, Amazon SES or Mailgun account, with SPF, DKIM and DMARC checks.',
        h1: 'Signing emails from your address, through your provider',
        lead: 'Invitations that come from your own domain get opened. Connect the email service you already use and sign.yt sends every notification through it.',
        sections: [
          {
            h2: 'Pick the provider you already trust',
            body: 'Start with managed delivery and nothing to configure. When you are ready, switch your organization to your own SMTP server, Microsoft 365 through the Graph API, your own Resend account, Amazon SES or Mailgun.\n\nProvider credentials are stored encrypted, never in plain text.\n\nEach organization picks one provider, so every invitation, reminder and completion email goes out the same way.',
          },
          {
            h2: 'Microsoft 365 without SMTP passwords',
            body: 'With the Graph API, sign.yt sends as a mailbox in your Microsoft 365 tenant using an Azure app registration with the Mail.Send permission. No SMTP password to store, and no basic authentication to keep enabled in your tenant.\n\nYou choose the sending address, for example contracts@yourcompany.com, so replies from recipients reach a real mailbox your team already watches.',
          },
          {
            h2: 'DNS records checked for you',
            body: 'The settings screen lists the SPF, DKIM and DMARC records your sender domain needs for the chosen provider, then looks them up live and shows which are in place. Fewer invitations end up in spam.\n\nAmazon SES DKIM records are fetched for your domain automatically, and for other providers the screen tells you exactly which values to copy into your DNS panel.',
          },
          {
            h2: 'Bounces do not go unnoticed',
            body: 'With managed delivery, bounced and spam-flagged emails mark the recipient as bounced, are written to the audit log and trigger a recipient.bounced webhook, so you can fix the address instead of waiting for a signature that will never come.\n\nThe same bounce information is visible on the document, next to the recipient. You find out as soon as the provider reports it, not after the signing deadline has passed.',
          },
        ],
        faq: [
          {
            q: 'Which email providers are supported?',
            a: 'Managed delivery, SMTP, Microsoft 365 via Graph API, Resend with your own account, Amazon SES and Mailgun.',
          },
          {
            q: 'Which SMTP ports can I use?',
            a: 'Ports 25, 465, 587 and 2525, with TLS.',
          },
          {
            q: 'Can teams use different sender names?',
            a: 'Yes. The sender name can be set per team through team branding.',
          },
          {
            q: 'Do I have to configure anything to start?',
            a: 'No. Managed delivery works out of the box; your own provider is optional.',
          },
        ],
      },
      pl: {
        name: 'Wysyłka e-maili',
        title: 'Wysyłka e-maili do podpisu z Twojej domeny',
        desc: 'Zaproszenia do podpisu przez Twój serwer SMTP, Microsoft 365 (Graph API), Resend, Amazon SES lub Mailgun, ze sprawdzaniem rekordów SPF, DKIM i DMARC.',
        h1: 'E-maile do podpisu z Twojego adresu, przez Twojego dostawcę',
        lead: 'Zaproszenie z Twojej domeny jest otwierane chętniej niż wiadomość od nieznanego nadawcy. Podłącz usługę pocztową, z której już korzystasz, a sign.yt wyśle przez nią każde powiadomienie.',
        sections: [
          {
            h2: 'Wybierz dostawcę, któremu ufasz',
            body: 'Zaczynasz od wysyłki zarządzanej przez nas, bez żadnej konfiguracji. Gdy chcesz, przełączasz organizację na własny serwer SMTP, Microsoft 365 przez Graph API, własne konto Resend, Amazon SES albo Mailgun.\n\nDane dostępowe do dostawcy przechowujemy w postaci zaszyfrowanej, nigdy otwartym tekstem.\n\nKażda organizacja wybiera jednego dostawcę, więc zaproszenia, przypomnienia i powiadomienia o zakończeniu wychodzą zawsze tą samą drogą.',
          },
          {
            h2: 'Microsoft 365 bez haseł SMTP',
            body: 'Przez Graph API sign.yt wysyła wiadomości jako skrzynka w Twoim tenancie Microsoft 365, korzystając z rejestracji aplikacji Azure z uprawnieniem Mail.Send. Nie przechowujesz hasła SMTP i nie musisz utrzymywać podstawowego uwierzytelniania w swoim tenancie.\n\nSam wybierasz adres nadawcy, np. umowy@twojafirma.pl, więc odpowiedzi odbiorców trafiają do prawdziwej skrzynki, którą Twój zespół już obsługuje. To najpewniejsza droga dla firm pracujących na M365.',
          },
          {
            h2: 'Rekordy DNS sprawdzane za Ciebie',
            body: 'Ekran ustawień pokazuje rekordy SPF, DKIM i DMARC, których Twoja domena potrzebuje dla wybranego dostawcy, a następnie sprawdza je na żywo i oznacza, które są już ustawione. Mniej zaproszeń ląduje w spamie.\n\nRekordy DKIM dla Amazon SES pobieramy automatycznie dla Twojej domeny, a przy innych dostawcach ekran pokazuje dokładnie, jakie wartości skopiować do panelu DNS.',
          },
          {
            h2: 'Odbicia nie przechodzą bez echa',
            body: 'Przy wysyłce zarządzanej odbite i oznaczone jako spam wiadomości oznaczają odbiorcę jako niedostarczonego, trafiają do dziennika audytu i wywołują webhook recipient.bounced. Poprawiasz adres, zamiast czekać na podpis, który nigdy nie nadejdzie.\n\nInformację o odbiciu widać też przy odbiorcy w szczegółach dokumentu. Dowiadujesz się o problemie, gdy tylko zgłosi go dostawca, a nie po upływie terminu na podpis. Dzięki temu możesz od razu poprawić adres i wysłać dokument ponownie.',
          },
        ],
        faq: [
          {
            q: 'Jacy dostawcy poczty są obsługiwani?',
            a: 'Wysyłka zarządzana, SMTP, Microsoft 365 przez Graph API, Resend z własnym kontem, Amazon SES i Mailgun.',
          },
          {
            q: 'Jakich portów SMTP mogę użyć?',
            a: 'Portów 25, 465, 587 i 2525, z szyfrowaniem TLS.',
          },
          {
            q: 'Czy zespoły mogą mieć różne nazwy nadawcy?',
            a: 'Tak. Nazwę nadawcy ustawisz dla każdego zespołu w jego brandingu.',
          },
          {
            q: 'Czy muszę coś konfigurować na start?',
            a: 'Nie. Wysyłka zarządzana działa od razu, własny dostawca jest opcjonalny.',
          },
        ],
      },
    },
  },
  {
    slug: 'document-templates',
    icon: 'FileText',
    group: 'docs',
    copy: {
      en: {
        name: 'Document templates',
        title: 'Reusable e-signature templates and public links',
        desc: 'Save contracts as templates with signers and fields in place. Reuse them in seconds, swap the PDF when the wording changes, or share a public signing link.',
        h1: 'Set up a contract once, send it a hundred times',
        lead: 'Templates keep the PDF, signer roles and field positions together. Sending the same agreement again takes a few clicks instead of a fresh setup.',
        sections: [
          {
            h2: 'From any document to a template',
            body: 'Upload a PDF and place signature, date, text and checkbox fields, or turn a document you already sent into a template. Signer roles and field positions are saved with it.\n\nWhen the contract wording changes but the layout stays, replace the PDF and keep every field where it was.\n\nTemplates are organised by category, so your team finds the right agreement quickly.',
          },
          {
            h2: 'Public links and QR codes',
            body: 'Turn on a public link for a template and anyone with the link can fill it in and sign, for example an onboarding form or a marketing consent. sign.yt also generates a QR code for the link, ready for a flyer or a reception desk.\n\nA public link can be switched off at any time and can be limited by expiry date or number of uses. Each signed copy is a separate document with its own audit trail.',
          },
          {
            h2: 'A head start for Polish SMBs',
            body: 'A starter catalog suggests roles, signing order, verification level and field layout for common documents in virtual offices, law firms, accounting, HR and GDPR. You upload your own PDF, the blueprint fills in the rest.\n\nIt is a faster start than an empty editor, especially for teams preparing their first templates. Categories reflect the industries we work with most.',
          },
          {
            h2: 'Fields placed for you',
            body: 'On request, sign.yt can analyse a page of your PDF and suggest where signature, date and text fields belong. You review the suggestion, adjust it and save.\n\nDetection is optional and runs only when you ask for it. The final layout is always your decision, and you can move, resize or delete any suggested field before the document goes out to recipients.',
          },
        ],
        faq: [
          {
            q: 'Can I update a template without redoing the fields?',
            a: 'Yes. Replace the PDF and the existing field positions are kept.',
          },
          {
            q: 'Who can sign through a public link?',
            a: 'Anyone who has the link, as long as it is enabled. You can switch it off at any time.',
          },
          {
            q: 'Is automatic field detection always on?',
            a: 'No. It runs only when you click the button in the field editor.',
          },
        ],
      },
      pl: {
        name: 'Szablony dokumentów',
        title: 'Szablony umów do podpisu i publiczne linki',
        desc: 'Zapisz umowę jako szablon z odbiorcami i polami. Wysyłaj ją w kilka sekund, podmieniaj PDF przy zmianie treści albo udostępnij publiczny link do podpisu.',
        h1: 'Przygotuj umowę raz, wysyłaj ją setki razy',
        lead: 'Szablon przechowuje PDF, role podpisujących i położenie pól. Ponowna wysyłka tej samej umowy to kilka kliknięć zamiast konfiguracji od zera.',
        sections: [
          {
            h2: 'Z dowolnego dokumentu do szablonu',
            body: 'Wgrywasz PDF i rozmieszczasz pola podpisu, daty, tekstu i zaznaczenia albo zamieniasz w szablon dokument, który już wysłałeś. Role podpisujących i położenie pól zapisują się razem z nim.\n\nGdy zmienia się treść umowy, ale układ zostaje, podmieniasz PDF, a wszystkie pola zostają na swoich miejscach.\n\nSzablony są uporządkowane według kategorii, więc zespół szybko znajduje właściwą umowę.',
          },
          {
            h2: 'Publiczne linki i kody QR',
            body: 'Włączasz publiczny link do szablonu i każdy, kto go ma, może wypełnić i podpisać dokument, np. formularz onboardingu albo zgodę marketingową. sign.yt generuje też kod QR do linku, gotowy do wydruku na ulotce lub w recepcji.\n\nPubliczny link możesz w każdej chwili wyłączyć, a także ograniczyć datą ważności albo liczbą użyć. Każdy podpisany egzemplarz to osobny dokument z własnym dziennikiem audytu.',
          },
          {
            h2: 'Gotowe wzorce dla polskich firm',
            body: 'Katalog startowy podpowiada role, kolejność podpisu, poziom weryfikacji i układ pól dla typowych dokumentów biur wirtualnych, kancelarii, księgowości, HR i RODO. Wgrywasz własny PDF, a wzorzec uzupełnia resztę.\n\nTo szybszy start niż pusty edytor, zwłaszcza dla zespołów, które przygotowują swoje pierwsze szablony. Kategorie odpowiadają typowym branżom naszych klientów, a każdy wzorzec możesz dalej dopasować do własnych potrzeb.',
          },
          {
            h2: 'Pola rozmieszczone za Ciebie',
            body: 'Na życzenie sign.yt przeanalizuje stronę PDF i zaproponuje miejsca na podpis, datę i pola tekstowe. Sprawdzasz propozycję, poprawiasz i zapisujesz.\n\nWykrywanie jest opcjonalne i działa tylko na Twoje polecenie. Ostateczny układ zawsze zależy od Ciebie: każde zaproponowane pole możesz przesunąć, zmienić jego rozmiar albo usunąć, zanim dokument trafi do odbiorców. Wynik jest zapamiętywany dla danego pliku.',
          },
        ],
        faq: [
          {
            q: 'Czy mogę zaktualizować szablon bez ponownego układania pól?',
            a: 'Tak. Podmieniasz PDF, a dotychczasowe położenie pól zostaje zachowane.',
          },
          {
            q: 'Kto może podpisać przez publiczny link?',
            a: 'Każdy, kto ma link, dopóki jest włączony. Możesz go wyłączyć w dowolnej chwili.',
          },
          {
            q: 'Czy automatyczne wykrywanie pól działa zawsze?',
            a: 'Nie. Uruchamia się tylko po kliknięciu przycisku w edytorze pól.',
          },
        ],
      },
    },
  },
  {
    slug: 'bulk-send-campaigns',
    icon: 'Send',
    group: 'docs',
    copy: {
      en: {
        name: 'Bulk send and campaigns',
        title: 'Bulk send documents for e-signature from CSV',
        desc: 'Send one document to hundreds of people from a CSV file. Throttled delivery, pause and resume, reminders, CSV export and a public QR campaign mode.',
        h1: 'One document, hundreds of signers, zero copy and paste',
        lead: 'Annual policy updates, new terms of service, consent refreshes. Campaigns send each person their own document and track every signature in one place.',
        sections: [
          {
            h2: 'Upload a CSV, check it, send',
            body: 'Prepare a CSV with email, name and phone, and sign.yt shows a preview before anything goes out. Each recipient gets a separate document, so one person\'s signature or refusal never blocks the others.\n\nTypical uses are annual NDA refreshes, updated terms for all clients or a new consent for every tenant. The preview flags problems in the file, such as a missing email column, before anything is sent.',
          },
          {
            h2: 'Sending at a safe pace',
            body: 'Campaigns are sent in the background at a per-organization rate shared with reminders, so a large list does not flood your mailbox or trip your provider\'s limits. You can pause, resume or cancel a running campaign at any time.\n\nSending is handled row by row, so even with several workers running nobody receives the same document twice.',
          },
          {
            h2: 'Follow up without a spreadsheet',
            body: 'The campaign view shows who has signed and who has not. Send a reminder to everyone still pending, retry failed sends, and export the results to CSV or the evidence to JSON for your records.\n\nFor public campaigns you can also add a list of people you expect to sign and see at a glance who is still missing.',
          },
          {
            h2: 'Public campaigns with a QR code',
            body: 'Need signatures from people you do not have on a list yet, for example event attendees or new tenants? A public campaign gives you a link and a QR code, and every visitor signs their own copy.\n\nEach copy carries its own signatures, verification and audit trail, exactly like a document sent from a list.',
          },
        ],
        faq: [
          {
            q: 'What columns does the CSV need?',
            a: 'Email, name and optionally phone, which is used for SMS verification.',
          },
          {
            q: 'Can I stop a campaign halfway?',
            a: 'Yes. Pause and resume it, or cancel the remaining sends.',
          },
          {
            q: 'Do reminders count against the send rate?',
            a: 'Yes. Campaigns and reminders share one rate per organization, which keeps delivery steady.',
          },
        ],
      },
      pl: {
        name: 'Wysyłka masowa i kampanie',
        title: 'Masowa wysyłka dokumentów do podpisu z CSV',
        desc: 'Wyślij jeden dokument do setek osób z pliku CSV. Wysyłka w bezpiecznym tempie, pauza i wznowienie, przypomnienia, eksport do CSV i kampanie z kodem QR.',
        h1: 'Jeden dokument, setki podpisów, zero kopiowania',
        lead: 'Coroczna aktualizacja regulaminu, nowe warunki usług, odnowienie zgód. Kampania wysyła każdej osobie jej własny dokument i śledzi wszystkie podpisy w jednym miejscu.',
        sections: [
          {
            h2: 'Wgraj CSV, sprawdź, wyślij',
            body: 'Przygotowujesz plik CSV z adresem e-mail, imieniem i nazwiskiem oraz telefonem, a sign.yt pokazuje podgląd, zanim cokolwiek wyjdzie. Każdy odbiorca dostaje osobny dokument, więc podpis lub odmowa jednej osoby nie blokuje pozostałych.\n\nTypowe zastosowania to coroczne odnowienie NDA, nowy regulamin dla wszystkich klientów albo nowa zgoda dla każdego najemcy. Podgląd sygnalizuje problemy z plikiem, np. brak kolumny z e-mailem, zanim cokolwiek zostanie wysłane.',
          },
          {
            h2: 'Wysyłka w bezpiecznym tempie',
            body: 'Kampanie wysyłają się w tle w tempie ustalonym dla organizacji i wspólnym z przypomnieniami, więc duża lista nie zapcha skrzynki ani nie przekroczy limitów dostawcy poczty. Trwającą kampanię możesz w każdej chwili wstrzymać, wznowić albo anulować.\n\nWysyłka jest przetwarzana wiersz po wierszu, więc nawet przy kilku procesach nikt nie dostanie tego samego dokumentu dwa razy.',
          },
          {
            h2: 'Przypomnienia bez arkusza kalkulacyjnego',
            body: 'Widok kampanii pokazuje, kto już podpisał, a kto jeszcze nie. Jednym kliknięciem wysyłasz przypomnienie do wszystkich oczekujących, ponawiasz nieudane wysyłki i eksportujesz wyniki do CSV albo dowody do JSON.\n\nW kampaniach publicznych możesz też dodać listę osób, od których oczekujesz podpisu, i od razu zobaczyć, kogo jeszcze brakuje. Masz pełną kontrolę bez arkuszy i ręcznego liczenia.',
          },
          {
            h2: 'Kampanie publiczne z kodem QR',
            body: 'Potrzebujesz podpisów od osób, których jeszcze nie masz na liście, np. uczestników wydarzenia albo nowych najemców? Kampania publiczna daje link i kod QR, a każda osoba podpisuje własny egzemplarz.\n\nKażdy egzemplarz ma własne podpisy, weryfikację i dziennik audytu, dokładnie tak jak dokument wysłany z listy. Kod QR wydrukujesz na ulotce, plakacie albo w recepcji. Działa na każdym telefonie.',
          },
        ],
        faq: [
          {
            q: 'Jakie kolumny musi mieć plik CSV?',
            a: 'E-mail, imię i nazwisko oraz opcjonalnie telefon, używany do weryfikacji SMS.',
          },
          {
            q: 'Czy mogę zatrzymać kampanię w trakcie?',
            a: 'Tak. Możesz ją wstrzymać i wznowić albo anulować pozostałe wysyłki.',
          },
          {
            q: 'Czy przypomnienia liczą się do tempa wysyłki?',
            a: 'Tak. Kampanie i przypomnienia dzielą jeden limit na organizację, co utrzymuje równe tempo dostarczania.',
          },
        ],
      },
    },
  },
  {
    slug: 'smart-documents-gus-vies',
    icon: 'Building2',
    group: 'docs',
    copy: {
      en: {
        name: 'Smart documents',
        title: 'Contract generator with NIP, GUS and VIES autofill',
        desc: 'Generate contracts from templates with variables. Enter a NIP and company data fills in from GUS, the MF VAT white list and VIES, then send for signature.',
        h1: 'Contracts that fill themselves in from a tax number',
        lead: 'Smart documents turn your contract wording into a form. Type a NIP, pull the company details from public registries, and generate ready-to-sign PDFs.',
        sections: [
          {
            h2: 'Company data from public registries',
            body: 'Enter a Polish NIP and sign.yt combines data from GUS (REGON, KRS, legal form, address) with the Ministry of Finance VAT white list (VAT status and bank accounts). For other EU countries it uses VIES.\n\nIf one source is unavailable, the others still fill in what they can and you see a warning instead of an error.',
          },
          {
            h2: 'Templates with real logic',
            body: 'Write the contract in the built-in editor or import a .docx file where placeholders in square brackets or double braces become variables. Variables have types such as money, date, NIP, IBAN or list, and you can add conditional sections, repeated rows and computed values.\n\nComputed values use a safe expression language, so totals, net and gross amounts or due dates are worked out for you. The finished document is rendered to PDF and goes through the regular signing flow.',
          },
          {
            h2: 'Polish formatting built in',
            body: 'Amounts are formatted the Polish way and can be written out in words, dates follow Polish conventions, and payments can be split into instalments automatically. No more manual "słownie" fields.\n\nValues are checked against their type before generation, so an invalid NIP or IBAN is caught in the form instead of in the signed contract. Every generated PDF looks like a document prepared by hand by your office.',
          },
          {
            h2: 'One form, a whole package',
            body: 'Group several documents into a package, such as an agreement, an annex and a GDPR clause. Shared variables are filled in once, and sign.yt generates and sends every document together.\n\nThe package form shows each variable only once, even when it appears in several documents. That removes copy and paste errors between the main agreement and its attachments, and the recipient signs the whole set in one flow.',
          },
        ],
        faq: [
          {
            q: 'Which registries are used?',
            a: 'GUS BIR (REGON database), the Ministry of Finance VAT white list and the EU VIES system.',
          },
          {
            q: 'Can signers fill in their own company details?',
            a: 'Yes. On the signing page a recipient can enter their NIP and fetch their company data from the registries.',
          },
          {
            q: 'Can I import my existing Word contracts?',
            a: 'Yes. Upload a .docx file and placeholders like [CLIENT NAME] or {{client.name}} become variables.',
          },
        ],
      },
      pl: {
        name: 'Inteligentne dokumenty',
        title: 'Generator umów z danymi z GUS, VIES i białej listy',
        desc: 'Generuj umowy z szablonów ze zmiennymi. Wpisz NIP, a dane firmy uzupełnią się z GUS, białej listy podatników VAT i VIES. Potem wyślij dokument do podpisu.',
        h1: 'Umowy, które uzupełniają się same po numerze NIP',
        lead: 'Inteligentne dokumenty zamieniają treść umowy w formularz. Wpisujesz NIP, pobierasz dane firmy z rejestrów publicznych i generujesz gotowe do podpisu pliki PDF.',
        sections: [
          {
            h2: 'Dane firmy z rejestrów publicznych',
            body: 'Wpisujesz NIP, a sign.yt łączy dane z GUS (REGON, KRS, forma prawna, adres) z białą listą podatników VAT Ministerstwa Finansów (status VAT i rachunki bankowe). Dla firm z innych krajów UE korzysta z VIES.\n\nGdy jedno źródło nie odpowiada, pozostałe i tak uzupełnią, co się da, a Ty zobaczysz ostrzeżenie zamiast błędu. Dane z rejestrów trafiają prosto do zmiennych dokumentu.',
          },
          {
            h2: 'Szablony z prawdziwą logiką',
            body: 'Treść umowy piszesz we wbudowanym edytorze albo importujesz plik .docx, w którym pola w nawiasach kwadratowych lub podwójnych klamrach stają się zmiennymi. Zmienne mają typy, np. kwota, data, NIP, IBAN czy lista, a do tego dochodzą sekcje warunkowe, powtarzane wiersze i wartości wyliczane.\n\nWartości wyliczane korzystają z bezpiecznego języka wyrażeń, więc sumy, kwoty netto i brutto czy terminy płatności liczą się same. Gotowy dokument jest renderowany do PDF i przechodzi przez zwykły obieg podpisu.',
          },
          {
            h2: 'Polskie formatowanie w standardzie',
            body: 'Kwoty są formatowane po polsku i mogą być zapisane słownie, daty mają polski format, a płatność można automatycznie rozbić na raty. Koniec z ręcznym wpisywaniem kwot słownie.\n\nWartości są sprawdzane według typu przed wygenerowaniem, więc błędny NIP lub IBAN wyłapiesz w formularzu, a nie w podpisanej umowie. Każdy wygenerowany PDF wygląda jak dokument przygotowany ręcznie w Twoim biurze, z poprawnym polskim formatowaniem.',
          },
          {
            h2: 'Jeden formularz, cały pakiet',
            body: 'Łączysz kilka dokumentów w pakiet, np. umowę, aneks i klauzulę RODO. Wspólne zmienne wypełniasz raz, a sign.yt generuje i wysyła wszystkie dokumenty razem.\n\nFormularz pakietu pokazuje każdą zmienną tylko raz, nawet jeśli występuje w kilku dokumentach. To eliminuje błędy przy przepisywaniu danych między umową główną a załącznikami, a odbiorca podpisuje cały komplet w jednym procesie. Idealne dla biur wirtualnych i rachunkowych.',
          },
        ],
        faq: [
          {
            q: 'Z jakich rejestrów korzystacie?',
            a: 'Z GUS BIR (baza REGON), białej listy podatników VAT Ministerstwa Finansów i unijnego systemu VIES.',
          },
          {
            q: 'Czy podpisujący może sam uzupełnić dane swojej firmy?',
            a: 'Tak. Na stronie podpisu odbiorca wpisuje swój NIP i pobiera dane firmy z rejestrów.',
          },
          {
            q: 'Czy mogę zaimportować swoje umowy z Worda?',
            a: 'Tak. Wgrywasz plik .docx, a pola takie jak [NAZWA KLIENTA] albo {{klient.nazwa}} stają się zmiennymi.',
          },
        ],
      },
    },
  },
  {
    slug: 'api-webhooks',
    icon: 'Webhook',
    group: 'dev',
    copy: {
      en: {
        name: 'API and webhooks',
        title: 'E-signature REST API with signed webhooks',
        desc: 'Create, send and track documents from your own software. Scoped API keys, HMAC-signed webhooks with automatic retries, and a versioned REST API.',
        h1: 'Put e-signatures inside the software you already run',
        lead: 'Your CRM, ERP or client portal can create documents, send them for signature and react the moment they are signed, without anyone opening sign.yt.',
        sections: [
          {
            h2: 'A versioned REST API',
            body: 'Everything lives under api.sign.yt/v1 and speaks JSON: documents, recipients, fields, templates and webhooks. Create a document from a template, send it and download the signed PDF in a few calls.\n\nEvery request is scoped to the organization that owns the API key, so one integration can never see another tenant\'s documents. Errors come back as structured JSON with clear status codes, which keeps client code simple.',
          },
          {
            h2: 'API keys with scopes',
            body: 'Each organization creates its own API keys and grants each key only the scopes it needs, read or write per area. Keys are stored hashed, only the prefix is shown after creation, and each key is limited to 100 requests per second.\n\nRevoke a key at any time and integrations using it stop immediately. The last-used time helps you spot keys nobody needs any more.',
          },
          {
            h2: 'Webhooks you can trust',
            body: 'Subscribe to events such as document.sent, document.viewed, document.signed, document.completed, document.rejected, document.expired and recipient.bounced. Every delivery is signed with HMAC-SHA256 over a timestamp and the raw body, so you can verify it came from sign.yt and reject replays.\n\nEndpoints are re-checked before each delivery to block requests to internal addresses. Each request also carries the event name and a delivery ID in its headers, so you can route and deduplicate events easily.',
          },
          {
            h2: 'Retries built in',
            body: 'If your endpoint is down, sign.yt retries after 1 minute, 5 minutes, 30 minutes, 2 hours and 12 hours. Each delivery has a stable ID for deduplication, and failed deliveries are kept so nothing is silently lost.\n\nEvery attempt is signed again with a fresh timestamp, so even a retry hours later passes the replay window check on your side.',
          },
        ],
        faq: [
          {
            q: 'How do I authenticate?',
            a: 'Send your API key as a Bearer token in the Authorization header.',
          },
          {
            q: 'How do I verify a webhook?',
            a: 'Compute HMAC-SHA256 of the timestamp header, a dot and the raw body with your webhook secret, and compare it with the X-Signyt-Signature header.',
          },
          {
            q: 'What happens when my endpoint fails?',
            a: 'sign.yt retries five times over about 15 hours, then marks the delivery as failed.',
          },
          {
            q: 'Which plans include API access?',
            a: 'API keys and webhooks are part of the Business plan and higher.',
          },
        ],
      },
      pl: {
        name: 'API i webhooki',
        title: 'REST API do podpisu elektronicznego i webhooki',
        desc: 'Twórz, wysyłaj i śledź dokumenty z własnego systemu. Klucze API z uprawnieniami, webhooki podpisane HMAC z ponowieniami i wersjonowane REST API.',
        h1: 'Podpis elektroniczny w systemach, których już używasz',
        lead: 'Twój CRM, ERP albo portal klienta może tworzyć dokumenty, wysyłać je do podpisu i reagować w chwili podpisania, bez otwierania sign.yt.',
        sections: [
          {
            h2: 'Wersjonowane REST API',
            body: 'Wszystko jest dostępne pod api.sign.yt/v1 w formacie JSON: dokumenty, odbiorcy, pola, szablony i webhooki. Utworzenie dokumentu z szablonu, wysyłka i pobranie podpisanego PDF to kilka wywołań.\n\nKażde zapytanie jest ograniczone do organizacji, do której należy klucz API, więc integracja nigdy nie zobaczy dokumentów innego klienta. Błędy wracają jako ustrukturyzowany JSON z czytelnymi kodami statusu, co upraszcza kod po Twojej stronie.',
          },
          {
            h2: 'Klucze API z zakresem uprawnień',
            body: 'Każda organizacja tworzy własne klucze API i nadaje każdemu tylko potrzebne uprawnienia, odczyt lub zapis dla danego obszaru. Klucze przechowujemy jako skróty, po utworzeniu widać tylko prefiks, a każdy klucz ma limit 100 zapytań na sekundę.\n\nKlucz możesz w każdej chwili unieważnić, a korzystające z niego integracje natychmiast przestają działać. Czas ostatniego użycia pomaga znaleźć klucze, których nikt już nie potrzebuje.',
          },
          {
            h2: 'Webhooki, którym możesz ufać',
            body: 'Subskrybujesz zdarzenia takie jak document.sent, document.viewed, document.signed, document.completed, document.rejected, document.expired czy recipient.bounced. Każde wywołanie jest podpisane HMAC-SHA256 ze znacznikiem czasu i surową treścią, więc sprawdzisz, że pochodzi od sign.yt, i odrzucisz powtórzenia.\n\nAdresy docelowe są sprawdzane przed każdą dostawą, co blokuje zapytania do adresów wewnętrznych. Każde wywołanie ma też w nagłówkach nazwę zdarzenia i identyfikator dostawy, co ułatwia kierowanie i deduplikację zdarzeń po Twojej stronie.',
          },
          {
            h2: 'Ponowienia w standardzie',
            body: 'Gdy Twój serwer nie odpowiada, sign.yt ponawia próbę po 1 minucie, 5 minutach, 30 minutach, 2 godzinach i 12 godzinach. Każde wywołanie ma stały identyfikator do deduplikacji, a nieudane dostawy są zachowywane, więc nic nie ginie po cichu.\n\nKażda próba jest podpisywana na nowo ze świeżym znacznikiem czasu, więc nawet ponowienie po kilku godzinach przejdzie po Twojej stronie kontrolę okna czasowego.',
          },
        ],
        faq: [
          {
            q: 'Jak się uwierzytelnić?',
            a: 'Przekaż klucz API jako token Bearer w nagłówku Authorization.',
          },
          {
            q: 'Jak zweryfikować webhook?',
            a: 'Oblicz HMAC-SHA256 z nagłówka znacznika czasu, kropki i surowej treści, używając sekretu webhooka, i porównaj wynik z nagłówkiem X-Signyt-Signature.',
          },
          {
            q: 'Co się dzieje, gdy mój serwer nie odpowiada?',
            a: 'sign.yt ponawia próbę pięć razy w ciągu około 15 godzin, a potem oznacza dostawę jako nieudaną.',
          },
          {
            q: 'Które plany obejmują API?',
            a: 'Klucze API i webhooki są dostępne od planu Business wzwyż.',
          },
        ],
      },
    },
  },
  {
    slug: 'audit-trail',
    icon: 'ShieldCheck',
    group: 'trust',
    copy: {
      en: {
        name: 'Audit trail',
        title: 'Tamper-evident audit trail for e-signatures',
        desc: 'Every action is logged in an append-only, hash-chained audit log. Signatures are chained per document and a certificate of completion is added to each PDF.',
        h1: 'An audit trail that shows if anyone tried to rewrite history',
        lead: 'Who sent it, who opened it, who verified and who signed, with IP addresses and timestamps. Records are chained with SHA-256, so a removed or altered entry breaks the chain.',
        sections: [
          {
            h2: 'Append-only, hash-chained log',
            body: 'Each audit entry stores the hash of the previous entry in your organization\'s chain. Entries are only ever added, never updated or deleted, which the database itself enforces, and each tenant has its own independent chain.\n\nIf someone removed or edited a record directly in the database, the chain would no longer verify. Events cover the whole lifecycle, from sign-in and settings changes to sending, viewing and signing.',
          },
          {
            h2: 'Signatures linked to each other',
            body: 'Every signature on a document points to the hash of the previous one. Before a document is marked as completed, sign.yt walks the whole chain and refuses to complete it if anything does not match.\n\nA broken chain is recorded in the audit log, so a problem is visible instead of being quietly sealed into a finished PDF. Together with the PAdES signatures this gives two independent ways to prove the order of events.',
          },
          {
            h2: 'Explicit consent and a Bitcoin anchor',
            body: 'Before signing, each signer ticks a consent statement. Its text, language and hash are stored with the signature and in the audit log, so you can show exactly what the person agreed to.\n\nWhen a document is completed, sign.yt anchors the hashes of the signed PDF, the audit trail and the head of the audit chain in Bitcoin through OpenTimestamps. The proof shows the files existed at that block time and can be checked with the open-source ots tool, without relying on sign.yt.',
          },
          {
            h2: 'Certificate of completion in the PDF',
            body: 'The signed PDF ends with a certificate of completion: each signer\'s name, email, role, verification method, when the document was sent, viewed and signed, the IP address and the browser. Your evidence travels with the file.\n\nBecause the certificate is part of the PDF, it stays with every copy of the document, wherever it is stored or forwarded. Its QR code and short link open a public verification page for the document.',
          },
          {
            h2: 'Find and export what you need',
            body: 'Filter the organization audit log by event type, or download the full history of a single document as CSV for your lawyer or auditor.\n\nThe evidence ZIP puts everything in one download: the signed PDF, a standalone audit trail, the OpenTimestamps proof, the platform seal certificate, a manifest of SHA-256 hashes and a README that explains how to verify each part. Signers can download it too.\n\nEntries record the actor, email and IP address, the target and the details of the event. Organization admins see only their own organization, and the log is paginated so even long histories stay fast to browse and search.',
          },
        ],
        faq: [
          {
            q: 'Can an audit entry be edited or deleted?',
            a: 'No. The log is append-only, and any change made outside the application breaks the hash chain.',
          },
          {
            q: 'What does the certificate of completion contain?',
            a: 'Signer identity, verification method, timestamps for sending, viewing and signing, IP address and user agent.',
          },
          {
            q: 'Can I export the audit trail?',
            a: 'Yes. Each document\'s history can be downloaded as CSV, and the full evidence bundle as a ZIP.',
          },
          {
            q: 'What is the Bitcoin anchor?',
            a: 'An OpenTimestamps proof that the document hashes existed when a given Bitcoin block was mined. It is confirmed within hours and can be verified independently of sign.yt.',
          },
        ],
      },
      pl: {
        name: 'Dziennik audytu',
        title: 'Dziennik audytu podpisów odporny na manipulacje',
        desc: 'Każda czynność trafia do dziennika audytu, w którym wpisy są tylko dopisywane i powiązane łańcuchem skrótów. Do PDF dołączamy certyfikat ukończenia.',
        h1: 'Dziennik audytu, który pokaże każdą próbę zmiany historii',
        lead: 'Kto wysłał, kto otworzył, kto się zweryfikował i kto podpisał, z adresami IP i czasem. Wpisy są powiązane skrótami SHA-256, więc usunięcie lub zmiana wpisu przerywa łańcuch.',
        sections: [
          {
            h2: 'Dziennik tylko do dopisywania, z łańcuchem skrótów',
            body: 'Każdy wpis audytu przechowuje skrót poprzedniego wpisu w łańcuchu Twojej organizacji. Wpisy są wyłącznie dodawane, nigdy aktualizowane ani usuwane, czego pilnuje sama baza danych, a każdy klient ma własny, niezależny łańcuch.\n\nGdyby ktoś usunął albo zmienił wpis bezpośrednio w bazie danych, łańcuch przestałby się zgadzać. Zdarzenia obejmują cały cykl pracy: od logowania i zmian ustawień, przez wysyłkę i otwarcie dokumentu, aż po podpis. Dzięki temu w razie kontroli odtworzysz pełną historię każdej umowy.',
          },
          {
            h2: 'Podpisy powiązane ze sobą',
            body: 'Każdy podpis na dokumencie wskazuje skrót poprzedniego. Zanim dokument zostanie oznaczony jako ukończony, sign.yt sprawdza cały łańcuch i nie zakończy procesu, jeśli coś się nie zgadza.\n\nPrzerwany łańcuch jest zapisywany w dzienniku audytu, więc problem jest widoczny, zamiast trafić po cichu do gotowego PDF. Razem z podpisami PAdES daje to dwa niezależne sposoby udowodnienia kolejności zdarzeń.',
          },
          {
            h2: 'Wyraźna zgoda i kotwica w Bitcoinie',
            body: 'Przed podpisem każdy podpisujący zaznacza oświadczenie o zgodzie. Jego treść, język i skrót są zapisywane przy podpisie i w dzienniku audytu, więc możesz pokazać, na co dokładnie ta osoba się zgodziła.\n\nPo ukończeniu dokumentu sign.yt zakotwicza skróty podpisanego PDF, ścieżki audytu i początku łańcucha audytu w Bitcoinie przez OpenTimestamps. Dowód pokazuje, że pliki istniały w chwili wydobycia bloku, i da się go sprawdzić otwartym narzędziem ots, bez polegania na sign.yt.',
          },
          {
            h2: 'Certyfikat ukończenia w pliku PDF',
            body: 'Podpisany PDF kończy się certyfikatem ukończenia: imię i nazwisko, e-mail i rola każdego podpisującego, metoda weryfikacji, czas wysłania, otwarcia i podpisu, adres IP oraz przeglądarka. Dowody są zawsze razem z plikiem.\n\nCertyfikat jest częścią pliku PDF, więc pozostaje z każdą kopią dokumentu, gdziekolwiek ją zapiszesz lub prześlesz. Na końcu certyfikatu znajduje się pieczęć platformy sign.yt, która zamyka dokument, a kod QR i krótki link otwierają publiczną stronę weryfikacji.',
          },
          {
            h2: 'Szukaj i eksportuj',
            body: 'Filtrujesz dziennik audytu organizacji według typu zdarzenia albo pobierasz pełną historię pojedynczego dokumentu w CSV dla prawnika lub audytora.\n\nPlik ZIP z dowodami zbiera wszystko w jednym pobraniu: podpisany PDF, osobną ścieżkę audytu, dowód OpenTimestamps, certyfikat pieczęci platformy, manifest skrótów SHA-256 i plik README z instrukcją weryfikacji każdej części. Podpisujący też mogą go pobrać.\n\nWpisy zawierają osobę wykonującą czynność, jej e-mail i adres IP, obiekt i szczegóły zdarzenia. Administratorzy organizacji widzą tylko własną organizację, a dziennik jest stronicowany, więc nawet długa historia przegląda się szybko. Eksport zajmuje kilka sekund i nie wymaga dostępu do bazy danych ani pomocy technicznej.',
          },
        ],
        faq: [
          {
            q: 'Czy wpis audytu można edytować lub usunąć?',
            a: 'Nie. Dziennik jest tylko do dopisywania, a każda zmiana poza aplikacją przerywa łańcuch skrótów.',
          },
          {
            q: 'Co zawiera certyfikat ukończenia?',
            a: 'Dane podpisującego, metodę weryfikacji, czas wysłania, otwarcia i podpisu, adres IP i przeglądarkę.',
          },
          {
            q: 'Czy mogę wyeksportować historię dokumentu?',
            a: 'Tak. Historię każdego dokumentu pobierzesz jako plik CSV, a pełny pakiet dowodów jako ZIP.',
          },
          {
            q: 'Czym jest kotwica w Bitcoinie?',
            a: 'To dowód OpenTimestamps, że skróty dokumentu istniały w chwili wydobycia określonego bloku Bitcoina. Potwierdzenie przychodzi w ciągu kilku godzin i można je sprawdzić niezależnie od sign.yt.',
          },
        ],
      },
    },
  },
  {
    slug: 'gdpr-rodo-compliance',
    icon: 'Lock',
    group: 'trust',
    copy: {
      en: {
        name: 'GDPR compliance',
        title: 'GDPR-ready e-signatures with encrypted storage',
        desc: 'Data export and erasure requests handled in the product, PDFs encrypted at rest with a separate key per organization, and strict tenant isolation.',
        h1: 'Privacy handled in the product, not in a spreadsheet',
        lead: 'Data subject requests, encryption and tenant isolation are part of sign.yt. You spend less time on GDPR paperwork and keep the signed documents the law asks you to keep.',
        sections: [
          {
            h2: 'Export on request',
            body: 'Users can download all their data as a ZIP file: profile, organizations, document and signature metadata, contacts and the audit entries that refer to them. People without an account, such as recipients, can request their data by email and receive a one-time download link.\n\nPublic requests are rate limited per email and per IP address, so nobody can flood the system.',
          },
          {
            h2: 'Erasure that keeps the evidence',
            body: 'When a user asks to be forgotten, sign.yt anonymises them: names and email addresses are replaced with opaque tags, sessions are revoked and login methods removed. Signed documents are kept, because deleting them would break the audit trail and the other parties\' rights.\n\nSuper admin accounts require an extra confirmation, which protects against accidental deletion.',
          },
          {
            h2: 'Encrypted at rest, per organization',
            body: 'Uploaded and signed PDFs are encrypted with AES-256-GCM using a key derived separately for each organization. Credentials for email and SMS providers are stored with field-level encryption, never in plain text.\n\nEach file is bound to its organization and document, so a blob copied to the wrong place cannot be decrypted there. Keys never live in the database next to the data they protect.',
          },
          {
            h2: 'Tenants never mix',
            body: 'Every query is scoped to the organization it belongs to. Requests on a custom domain resolve to exactly one organization, and audit chains are kept per tenant.\n\nThis isolation is what lets many companies, and many brands, share one platform without sharing data. Your clients\' contracts are visible only to the people you invite into your organization.',
          },
        ],
        faq: [
          {
            q: 'Can recipients without an account exercise their rights?',
            a: 'Yes. They submit a request with their email address and receive a link by email. The response never reveals whether the address exists in the system.',
          },
          {
            q: 'Are signed documents deleted on erasure?',
            a: 'No. Personal data is anonymised, but signed documents are kept to preserve the evidence and the rights of the other parties.',
          },
          {
            q: 'Is my data encrypted?',
            a: 'Yes. Documents are encrypted at rest with AES-256-GCM and a separate key per organization.',
          },
        ],
      },
      pl: {
        name: 'RODO',
        title: 'Podpis elektroniczny zgodny z RODO',
        desc: 'Eksport i usuwanie danych na żądanie wbudowane w produkt, pliki PDF szyfrowane osobnym kluczem dla każdej organizacji i ścisła izolacja klientów.',
        h1: 'RODO obsłużone w produkcie, a nie w arkuszu kalkulacyjnym',
        lead: 'Żądania osób, których dane dotyczą, szyfrowanie i izolacja klientów są częścią sign.yt. Mniej czasu poświęcasz na formalności, a podpisane dokumenty, które musisz przechowywać, zostają bezpieczne.',
        sections: [
          {
            h2: 'Eksport danych na żądanie',
            body: 'Użytkownik pobiera wszystkie swoje dane w pliku ZIP: profil, organizacje, metadane dokumentów i podpisów, kontakty oraz dotyczące go wpisy audytu. Osoby bez konta, np. odbiorcy dokumentów, mogą złożyć wniosek e-mailem i dostają jednorazowy link do pobrania.\n\nWnioski publiczne mają limity na adres e-mail i adres IP, więc nikt nie zaleje systemu żądaniami. Link działa jednorazowo.',
          },
          {
            h2: 'Prawo do bycia zapomnianym bez utraty dowodów',
            body: 'Gdy użytkownik prosi o usunięcie danych, sign.yt je anonimizuje: imiona, nazwiska i adresy e-mail zastępujemy nieprzejrzystymi znacznikami, sesje są unieważniane, a metody logowania usuwane. Podpisane dokumenty zostają, bo ich usunięcie przerwałoby ścieżkę audytu i naruszyło prawa drugiej strony. Konta super administratora wymagają dodatkowego potwierdzenia, co chroni przed przypadkowym usunięciem. Cały proces odbywa się w aplikacji, bez ręcznych operacji na bazie danych.',
          },
          {
            h2: 'Szyfrowanie danych w spoczynku, osobno dla każdej organizacji',
            body: 'Wgrane i podpisane pliki PDF są szyfrowane algorytmem AES-256-GCM kluczem wyprowadzanym osobno dla każdej organizacji. Dane dostępowe do dostawców e-mail i SMS przechowujemy z szyfrowaniem na poziomie pól, nigdy otwartym tekstem.\n\nKażdy plik jest powiązany ze swoją organizacją i dokumentem, więc skopiowany w inne miejsce nie da się tam odszyfrować. Klucze nigdy nie leżą w bazie obok danych, które chronią.',
          },
          {
            h2: 'Dane klientów nigdy się nie mieszają',
            body: 'Każde zapytanie jest ograniczone do organizacji, do której należy. Żądania na własnej domenie trafiają do dokładnie jednej organizacji, a łańcuchy audytu są prowadzone osobno dla każdego klienta.\n\nTa izolacja pozwala wielu firmom i wielu markom korzystać z jednej platformy bez dzielenia się danymi. Umowy Twoich klientów widzą tylko osoby, które zaprosisz do swojej organizacji. To podstawa całej platformy.',
          },
        ],
        faq: [
          {
            q: 'Czy odbiorcy bez konta mogą realizować swoje prawa?',
            a: 'Tak. Składają wniosek, podając adres e-mail, i dostają link w wiadomości. Odpowiedź nigdy nie zdradza, czy adres istnieje w systemie.',
          },
          {
            q: 'Czy przy usunięciu danych kasujecie podpisane dokumenty?',
            a: 'Nie. Dane osobowe są anonimizowane, a podpisane dokumenty zostają, by zachować dowody i prawa pozostałych stron.',
          },
          {
            q: 'Czy moje dane są szyfrowane?',
            a: 'Tak. Dokumenty są szyfrowane w spoczynku algorytmem AES-256-GCM z osobnym kluczem dla każdej organizacji.',
          },
        ],
      },
    },
  },
]

// Missing pack entry falls back to EN (build check in prerender-seo flags it).
export const FEATURES: Feature[] = RAW.map((f) => ({
  ...f,
  copy: {
    ...f.copy,
    de: PACKS.de.features[f.slug] ?? f.copy.en,
    es: PACKS.es.features[f.slug] ?? f.copy.en,
    uk: PACKS.uk.features[f.slug] ?? f.copy.en,
  },
}))

export function getFeature(slug: string): Feature | undefined {
  return FEATURES.find((f) => f.slug === slug)
}

/** Up to 3 related features: same group first, then the next ones in list order. */
export function relatedFeatures(f: Feature, has: (c: Capability) => boolean = () => false): Feature[] {
  const i = FEATURES.indexOf(f)
  const ring = [...FEATURES.slice(i + 1), ...FEATURES.slice(0, i)].filter((x) => visible(x, has))
  return [...ring.filter((x) => x.group === f.group), ...ring.filter((x) => x.group !== f.group)].slice(0, 3)
}
